<template>
  <div>
    <div class="page-header">
      <div class="header-left">
        <button class="btn btn-secondary back-button" @click="goBack">
          <i class="fa fa-arrow-left"></i> Back
        </button>
        <h4 class="table-page-header caregiver-name">{{ caregiverName }}</h4>

        <!-- Tab Buttons in Header -->
        <div class="button-toggle-row">
        <el-button
          :type="activeTab === 'calendar' ? 'primary' : 'default'"
          @click="activeTab = 'calendar'"
          class="toggle-button"
        >
          Calendar
        </el-button>
        <el-button
          :type="activeTab === 'work-schedule' ? 'primary' : 'default'"
          @click="activeTab = 'work-schedule'"
          class="toggle-button"
        >
          Work Schedule
        </el-button>
        <el-button
          v-if="canViewCompliance"
          :type="activeTab === 'training' ? 'primary' : 'default'"
          @click="activeTab = 'training'; loadTrainingTabData();"
          class="toggle-button"
        >
          Training
        </el-button>
        <el-button
          :type="activeTab === 'details' ? 'primary' : 'default'"
          @click="activeTab = 'details'"
          class="toggle-button"
        >
          Details
        </el-button>
        <el-button
          v-if="canViewCompliance"
          :type="activeTab === 'compliance' ? 'primary' : 'default'"
          @click="activeTab = 'compliance'; loadComplianceProfile();"
          class="toggle-button"
        >
          Compliance
        </el-button>
        <el-button
          v-if="isAdmin"
          :type="activeTab === 'client-access' ? 'primary' : 'default'"
          @click="activeTab = 'client-access'; loadClientAccess();"
          class="toggle-button"
        >
          Client Access
        </el-button>
        </div>
      </div>

      <!-- Change Password Button (for leads, caregivers, and employment specialists viewing their own profile) -->
      <div class="header-right">
        <button
          v-if="isViewingOwnPage && !isAdmin"
          @click="showPasswordChangeModal = true"
          class="btn-change-password"
        >
          <i class="fa fa-lock"></i>
          Change Password
        </button>
      </div>
    </div>

    <!-- Tab Content -->
    <div class="caregiver-tab-content">
      <!-- Calendar Tab -->
      <div v-if="activeTab === 'calendar'" class="caregiver-tab-pane">
        <div class="caregiver-schedule-container">
          <div class="schedule-content">
            <div class="modern-calendar-wrapper">
              <!-- Add New Shift Button -->
              <div class="calendar-header-actions" v-if="canAddShift">
                <el-button
                  type="primary"
                  size="small"
                  @click="createShift"
                  class="add-shift-button"
                >
                  <i class="fa-solid fa-plus"></i> Add New Shift
                </el-button>
              </div>

              <!-- Modern Legend -->
              <div class="modern-legend-row">
                <div class="legend-groups">
                  <div class="legend-group">
                    <div class="legend-group-header">
                      <i class="fas fa-calendar-check"></i>
                      <span>Schedule:</span>
                    </div>
                    <div class="legend-item">
                      <div class="legend-color work-schedule"></div>
                      <span>Work Hours</span>
                    </div>
                    <div class="legend-item">
                      <div class="legend-color time-off"></div>
                      <span>Time Off</span>
                    </div>
                  </div>
                  <div class="legend-group">
                    <div class="legend-group-header">
                      <i class="fas fa-info-circle"></i>
                      <span>Shifts:</span>
                    </div>
                    <div class="legend-item">
                      <div class="legend-color status-scheduled"></div>
                      <span>Scheduled</span>
                    </div>
                    <div class="legend-item">
                      <div class="legend-color status-clocked-in"></div>
                      <span>Clocked In</span>
                    </div>
                    <div class="legend-item">
                      <div class="legend-color status-clocked-out"></div>
                      <span>Clocked Out</span>
                    </div>
                    <div class="legend-item">
                      <div class="legend-color status-approved"></div>
                      <span>Approved</span>
                    </div>
                    <div class="legend-item">
                      <div class="legend-color status-missed"></div>
                      <span>Missed</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Month Navigation Controls -->
              <div class="month-navigation">
                <button @click="goToPreviousMonth" class="nav-btn">
                  <i class="fas fa-chevron-left"></i>
                </button>
                <div class="current-month-display">
                  {{ currentMonthYear }}
                </div>
                <button @click="goToNextMonth" class="nav-btn">
                  <i class="fas fa-chevron-right"></i>
                </button>
              </div>

              <div class="calendar-view">
                <MonthView
                  v-if="!loadingShifts"
                  :currentMonth="currentMonth"
                  :daysInMonth="daysInMonth"
                  :dayNames="dayNames"
                  :eventsForDate="shiftsForDate"
                  :isToday="isToday"
                  :workSchedule="caregiverSchedule"
                  :getScheduledHours="getScheduledHoursForDate"
                  :approvedTimeOff="caregiverApprovedTimeOff"
                  :showWeeklySummary="true"
                  pageType="caregiver"
                  @show-details="viewShiftDetails"
                />
                <div v-else class="text-center p-4">
                  <i class="fa fa-spinner fa-spin"></i> Loading schedule...
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Work Schedule Tab -->
      <div v-if="activeTab === 'work-schedule'" class="caregiver-tab-pane">
        <div class="schedule-editor" v-if="isAdmin">
          <!-- Schedule Management -->
          <div
            class="schedule-management"
            v-if="activeAndFutureSchedules.length > 0 || caregiverSchedule.id || pendingScheduleChangeRequests.length > 0"
          >
            <div class="schedule-selector">
              <div class="schedule-tabs">
                <button
                  v-for="schedule in displayActiveSchedules"
                  :key="schedule.id"
                  @click="adminScheduleViewMode = 'current'; selectSchedule(caregiverSchedules.indexOf(schedule))"
                  :class="[
                    'schedule-tab',
                    {
                      active:
                        adminScheduleViewMode === 'current' &&
                        selectedScheduleIndex ===
                        caregiverSchedules.indexOf(schedule),
                    },
                  ]"
                >
                  {{ formatScheduleLabel(schedule) }}
                </button>
                <button
                  v-if="pendingScheduleChangeRequests.length > 0"
                  @click="adminScheduleViewMode = 'requested'"
                  :class="['schedule-tab', { active: adminScheduleViewMode === 'requested' }]"
                >
                  Requested Schedule{{ hasCurrentSchedule ? ' Change' : '' }}{{ pendingScheduleChangeRequests.length > 1 ? ` (${pendingScheduleChangeRequests.length})` : '' }}
                </button>
                <button
                  v-if="adminScheduleViewMode !== 'requested'"
                  @click="adminScheduleViewMode = 'current'; createNewSchedule()"
                  :class="['schedule-tab', { active: adminScheduleViewMode === 'current' && selectedScheduleIndex === -1 }]"
                >
                  Add New Schedule
                </button>
              </div>
            </div>
          </div>

          <!-- Requested Schedule Change Review (admin inline) -->
          <div v-if="adminScheduleViewMode === 'requested' && pendingScheduleChangeRequests.length > 0">
            <!-- Request selector if multiple -->
            <div v-if="pendingScheduleChangeRequests.length > 1" class="schedule-tabs mb-3">
              <button
                v-for="(req, idx) in pendingScheduleChangeRequests"
                :key="req.id"
                @click="selectedPendingRequestIndex = idx"
                :class="['schedule-tab', { active: selectedPendingRequestIndex === idx }]"
              >
                Request {{ idx + 1 }} - {{ formatDateDisplay(req.effective_date) }}
              </button>
            </div>

            <!-- Request Details -->
            <div class="pending-requests-section mb-3">
              <div class="request-info">
                <div class="request-date">
                  <strong>Effective Date:</strong>
                  {{ formatDateDisplay(selectedPendingRequest.effective_date) }}
                </div>
                <div class="request-reason" v-if="selectedPendingRequest.reason">
                  <strong>Reason:</strong> {{ selectedPendingRequest.reason }}
                </div>
                <div class="request-submitted">
                  <strong>Submitted:</strong>
                  {{ formatDateDisplay(selectedPendingRequest.createdAt) }}
                </div>
              </div>
            </div>

            <!-- Read-only Schedule Grid -->
            <div class="schedule-calendar-grid view-only">
              <div
                v-for="dayConfig in dayConfigs"
                :key="dayConfig.key + '-header'"
                class="schedule-day-header"
              >
                <span>{{ dayConfig.short }}</span>
              </div>
              <div class="schedule-day-header weekly-summary-header">
                <span>Total Hours</span>
              </div>

              <div
                v-for="dayConfig in dayConfigs"
                :key="dayConfig.key + '-cell'"
                class="schedule-day-cell"
              >
                <div
                  class="day-content"
                  :class="{ 'day-enabled': getRequestShifts(dayConfig.key).length > 0 }"
                >
                  <div
                    v-if="getRequestShifts(dayConfig.key).length > 0"
                    class="multiple-shifts-display"
                  >
                    <div
                      v-for="(shift, shiftIndex) in getRequestShifts(dayConfig.key)"
                      :key="`${dayConfig.key}-shift-${shiftIndex}`"
                      class="shift-display-row"
                    >
                      <div class="shift-times-display">
                        <div class="time-display">{{ formatTime(shift.start) }}</div>
                        <span class="time-separator">-</span>
                        <div class="time-display">{{ formatTime(shift.end) }}</div>
                      </div>
                    </div>
                  </div>
                  <div v-else class="day-disabled-text">Off</div>
                </div>
              </div>

              <div class="schedule-day-cell weekly-summary-cell">
                <div class="day-content">
                  <div class="weekly-summary-content">
                    <div class="summary-total">{{ requestedWeeklyTotal }}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Approve / Deny Buttons -->
            <div class="text-center mt-4">
              <el-button
                type="success"
                round
                @click="approveScheduleRequest"
                :disabled="reviewSubmitting"
              >
                <i class="fa fa-check"></i> {{ reviewSubmitting ? 'Submitting...' : 'Approve' }}
              </el-button>
              <el-button
                type="danger"
                round
                @click="denyScheduleRequest"
                :disabled="reviewSubmitting"
              >
                <i class="fa fa-times"></i> Deny
              </el-button>
              <el-button
                v-if="hasCurrentSchedule"
                round
                @click="adminScheduleViewMode = 'current'"
              >
                Back to Current
              </el-button>
            </div>
          </div>

          <!-- Current Schedule Editor -->
          <div v-show="adminScheduleViewMode === 'current'">

          <!-- Date Range Selection -->
          <div class="schedule-date-range">
            <div class="date-range-grid">
              <div class="date-input-group">
                <label class="time-label">Start Date</label>
                <input
                  type="date"
                  v-model="caregiverSchedule.start_date"
                  :min="todayDate"
                  class="date-input"
                />
              </div>
              <div class="date-input-group">
                <label class="time-label">End Date (Optional)</label>
                <input
                  type="date"
                  v-model="caregiverSchedule.end_date"
                  :min="caregiverSchedule.start_date || todayDate"
                  class="date-input"
                />
              </div>
            </div>
            <div class="date-range-grid mt-2" v-if="selectedScheduleIndex !== -1 && (caregiverSchedule.creator || caregiverSchedule.approved_by || (caregiverSchedule.is_upcoming && caregiverSchedule.reviewer))">
              <div class="date-input-group" v-if="caregiverSchedule.creator">
                <label class="time-label">Created By</label>
                <div class="date-display">
                  {{ caregiverSchedule.creator.first_name }} {{ caregiverSchedule.creator.last_name }}
                </div>
              </div>
              <div class="date-input-group" v-if="caregiverSchedule.approved_by || (caregiverSchedule.is_upcoming && caregiverSchedule.reviewer)">
                <label class="time-label">Approved By</label>
                <div class="date-display">
                  {{ (caregiverSchedule.approved_by || caregiverSchedule.reviewer).first_name }}
                  {{ (caregiverSchedule.approved_by || caregiverSchedule.reviewer).last_name }}
                </div>
              </div>
              <div class="date-input-group" v-if="caregiverSchedule.approved_at || caregiverSchedule.reviewed_at">
                <label class="time-label">Approved On</label>
                <div class="date-display">
                  {{ formatDateDisplay(caregiverSchedule.approved_at || caregiverSchedule.reviewed_at) }}
                </div>
              </div>
            </div>
          </div>

          <!-- Timezone Display (shown when mismatch or when timezone needs to be set) -->
          <div v-if="showScheduleTimezoneWarning || !scheduleTimezone" class="schedule-date-range" style="margin-top: 10px;">
            <div class="date-range-grid">
              <div class="date-input-group" style="max-width: 350px;">
                <label class="time-label">Timezone</label>
                <div v-if="inferredCaregiverTimezone" class="date-display">
                  {{ formatTimezoneLabel(inferredCaregiverTimezone) }}
                </div>
                <AIVSelect
                  v-else
                  :value="scheduleTimezone"
                  @input="scheduleTimezone = $event"
                  :options="timezoneOptions"
                  placeholder="Select timezone (required)"
                  :custom-style="{ backgroundColor: '#656565', minWidth: '100%' }"
                />
              </div>
            </div>
            <div v-if="showScheduleTimezoneWarning" style="margin-top: 8px; margin-left: 4px; padding: 8px 12px; border-left: 3px solid #f56c6c; background: rgba(245, 108, 108, 0.08); border-radius: 0 4px 4px 0;">
              <div style="color: #f56c6c; font-weight: bold; font-size: 13px;">⚠ Your browser is in {{ shortTzName(browserTimezone) }}, but this schedule is in {{ shortTzName(scheduleTimezone) }}.</div>
              <div v-if="scheduleTimezoneConversionHint" style="color: #e6a23c; font-size: 12px; margin-top: 4px; line-height: 1.5;">
                {{ scheduleTimezoneConversionHint }}
              </div>
            </div>
          </div>

          <!-- Schedule Grid -->
          <div class="schedule-calendar-grid">
            <!-- Day headers with checkboxes row (Sun through Sat) -->
            <div
              v-for="dayConfig in dayConfigs"
              :key="dayConfig.key + '-header'"
              class="schedule-day-header"
            >
              <label class="header-checkbox-label">
                <span>{{ dayConfig.short }}</span>
                <input
                  type="checkbox"
                  :checked="caregiverSchedule[dayConfig.key + '_enabled']"
                  @change="
                    onDayEnabledChange(dayConfig.key, $event.target.checked)
                  "
                />
                <span class="schedule-checkmark"></span>
              </label>
            </div>

            <!-- Total Hours Header -->
            <div class="schedule-day-header weekly-summary-header">
              <span>Total Hours</span>
            </div>

            <!-- Day cells with multiple shift support -->
            <div
              v-for="dayConfig in dayConfigs"
              :key="dayConfig.key + '-cell'"
              class="schedule-day-cell"
            >
              <div
                class="day-content"
                :class="{
                  'day-enabled': caregiverSchedule[dayConfig.key + '_enabled'],
                }"
              >
                <div
                  v-if="caregiverSchedule[dayConfig.key + '_enabled']"
                  class="multiple-shifts-controls"
                >
                  <div
                    v-for="(shift, shiftIndex) in getShiftsForDay(
                      dayConfig.key,
                    )"
                    :key="`${dayConfig.key}-shift-${shiftIndex}`"
                    class="shift-row"
                  >
                    <div
                      v-if="getShiftsForDay(dayConfig.key).length > 1"
                      class="shift-remove-row"
                    >
                      <button
                        @click="removeShift(dayConfig.key, shiftIndex)"
                        class="remove-shift-btn"
                        title="Remove shift"
                      >
                        <i class="fa fa-times"></i>
                      </button>
                    </div>
                    <div class="shift-times">
                      <div class="time-input-group">
                        <label class="time-label">Start</label>
                        <AIVTimeSelect
                          :value="extractTimeFromDateTime(shift.start)"
                          @input="
                            updateShiftTime(
                              dayConfig.key,
                              shiftIndex,
                              'start',
                              $event,
                            )
                          "
                          :picker-options="{
                            selectableRange: '00:00:00 - 23:59:59',
                            format: 'HH:mm',
                          }"
                          placeholder="Start"
                          size="mini"
                          format="HH:mm"
                          value-format="HH:mm"
                        />
                      </div>
                      <div class="time-input-group">
                        <label class="time-label">End</label>
                        <AIVTimeSelect
                          :value="extractTimeFromDateTime(shift.end)"
                          @input="
                            updateShiftTime(
                              dayConfig.key,
                              shiftIndex,
                              'end',
                              $event,
                            )
                          "
                          :picker-options="{
                            selectableRange: '00:00:00 - 23:59:59',
                            format: 'HH:mm',
                          }"
                          placeholder="End"
                          size="mini"
                          format="HH:mm"
                          value-format="HH:mm"
                        />
                      </div>
                    </div>
                  </div>
                  <button
                    @click="addShift(dayConfig.key)"
                    class="add-shift-btn"
                    title="Add another shift"
                  >
                    <i class="fa fa-plus"></i> Add Shift
                  </button>
                </div>
                <div v-else class="day-disabled-text">Off</div>
              </div>
            </div>

            <!-- Total Hours Cell -->
            <div class="schedule-day-cell weekly-summary-cell">
              <div class="day-content">
                <div class="weekly-summary-content">
                  <div class="summary-total">{{ weeklyScheduledTotal }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Notes Section -->
          <div class="notes-section mt-4">
            <AIVRow
              :record.sync="caregiverSchedule"
              :fields="[
                {
                  label: 'Schedule Notes',
                  key: 'notes',
                  type: 'textarea',
                  rows: 3,
                },
              ]"
            />
          </div>

          <!-- Action Buttons -->
          <div class="text-center mt-4">
            <el-button round @click="cancelScheduleEdit">Cancel</el-button>
            <el-button
              v-if="caregiverSchedule.id"
              type="danger"
              round
              @click="deleteCurrentSchedule"
            >
              <i class="fa fa-trash"></i> Delete Schedule
            </el-button>
            <el-button type="primary" round @click="saveSchedule">
              {{ caregiverSchedule.id ? 'Update Schedule' : 'Add Schedule' }}
            </el-button>
          </div>
          </div>
        </div>
        <!-- View-Only Schedule for non-admins -->
        <div v-else class="schedule-viewer">
          <!-- Schedule Mode Selector -->
          <div class="schedule-mode-selector" v-if="hasCurrentSchedule || activeAndFutureSchedules.length > 0 || (isViewingOwnPage && pendingScheduleChangeRequests.length > 0)">
            <div class="schedule-tabs">
              <button
                v-for="schedule in displayActiveSchedules"
                :key="schedule.id"
                @click="scheduleViewMode = 'current'; selectSchedule(caregiverSchedules.indexOf(schedule))"
                :class="[
                  'schedule-tab',
                  { active: scheduleViewMode === 'current' && selectedScheduleIndex === caregiverSchedules.indexOf(schedule) },
                ]"
              >
                {{ formatScheduleLabel(schedule) }}
              </button>
              <button
                v-if="isViewingOwnPage && pendingScheduleChangeRequests.length > 0"
                @click="scheduleViewMode = 'pending'"
                :class="[
                  'schedule-tab',
                  { active: scheduleViewMode === 'pending' },
                ]"
              >
                Pending Request
              </button>
              <button
                v-if="isViewingOwnPage && pendingScheduleChangeRequests.length === 0"
                @click="scheduleViewMode = 'requested'"
                :class="[
                  'schedule-tab',
                  { active: scheduleViewMode === 'requested' },
                ]"
              >
                Request Schedule{{ hasCurrentSchedule ? ' Change' : '' }}
              </button>
            </div>
          </div>

          <!-- Pending Request Display (caregiver view) -->
          <div v-if="scheduleViewMode === 'pending' && pendingScheduleChangeRequests.length > 0">
            <div class="pending-requests-section mb-3">
              <div class="request-info">
                <div class="request-date">
                  <strong>Effective Date:</strong>
                  {{ formatDateDisplay(pendingScheduleChangeRequests[0].effective_date) }}
                </div>
                <div class="request-reason" v-if="pendingScheduleChangeRequests[0].reason">
                  <strong>Reason:</strong> {{ pendingScheduleChangeRequests[0].reason }}
                </div>
                <div class="request-submitted">
                  <strong>Submitted:</strong>
                  {{ formatDateDisplay(pendingScheduleChangeRequests[0].createdAt) }}
                </div>
                <div class="request-status mt-2">
                  <strong>Status:</strong>
                  <span style="color: #e6a23c; font-weight: bold;">Pending</span>
                </div>
              </div>
            </div>

            <!-- Read-only Schedule Grid -->
            <div class="schedule-calendar-grid view-only">
              <div
                v-for="dayConfig in dayConfigs"
                :key="dayConfig.key + '-pending-header'"
                class="schedule-day-header"
              >
                <span>{{ dayConfig.short }}</span>
              </div>
              <div class="schedule-day-header weekly-summary-header">
                <span>Total Hours</span>
              </div>

              <div
                v-for="dayConfig in dayConfigs"
                :key="dayConfig.key + '-pending-cell'"
                class="schedule-day-cell"
              >
                <div
                  class="day-content"
                  :class="{ 'day-enabled': getPendingShifts(dayConfig.key).length > 0 }"
                >
                  <div
                    v-if="getPendingShifts(dayConfig.key).length > 0"
                    class="multiple-shifts-display"
                  >
                    <div
                      v-for="(shift, shiftIndex) in getPendingShifts(dayConfig.key)"
                      :key="`${dayConfig.key}-pending-shift-${shiftIndex}`"
                      class="shift-display-row"
                    >
                      <div class="shift-times-display">
                        <div class="time-display">{{ formatTime(shift.start) }}</div>
                        <span class="time-separator">-</span>
                        <div class="time-display">{{ formatTime(shift.end) }}</div>
                      </div>
                    </div>
                  </div>
                  <div v-else class="day-disabled-text">Off</div>
                </div>
              </div>

              <div class="schedule-day-cell weekly-summary-cell">
                <div class="day-content">
                  <div class="weekly-summary-content">
                    <div class="summary-total">{{ pendingWeeklyTotal }}</div>
                  </div>
                </div>
              </div>
            </div>

            <div class="notes-section mt-4" v-if="pendingScheduleChangeRequests[0].reason">
              <label class="time-label">Reason for Change</label>
              <div class="notes-display">{{ pendingScheduleChangeRequests[0].reason }}</div>
            </div>
          </div>

          <!-- Current Schedule Display (read-only) -->
          <div v-if="scheduleViewMode === 'current'">

            <!-- Date Range Display (read-only) -->
            <div
              class="schedule-date-range view-only"
              v-if="caregiverSchedule.id"
            >
              <div class="date-range-grid">
                <div class="date-input-group">
                  <label class="time-label">Start Date</label>
                  <div class="date-display">
                    {{ formatDateDisplay(caregiverSchedule.start_date) }}
                  </div>
                </div>
                <div class="date-input-group">
                  <label class="time-label">End Date</label>
                  <div class="date-display">
                    {{
                      formatDateDisplay(caregiverSchedule.end_date) || "Ongoing"
                    }}
                  </div>
                </div>
              </div>
              <div class="date-range-grid mt-2" v-if="caregiverSchedule.creator">
                <div class="date-input-group">
                  <label class="time-label">Created By</label>
                  <div class="date-display">
                    {{ caregiverSchedule.creator.first_name }} {{ caregiverSchedule.creator.last_name }}
                  </div>
                </div>
              </div>
              <div class="date-range-grid mt-2" v-if="caregiverSchedule.approved_by || (caregiverSchedule.is_upcoming && caregiverSchedule.reviewer)">
                <div class="date-input-group">
                  <label class="time-label">Approved By</label>
                  <div class="date-display">
                    {{ (caregiverSchedule.approved_by || caregiverSchedule.reviewer).first_name }}
                    {{ (caregiverSchedule.approved_by || caregiverSchedule.reviewer).last_name }}
                  </div>
                </div>
                <div v-if="caregiverSchedule.approved_at || caregiverSchedule.reviewed_at" class="date-input-group">
                  <label class="time-label">Approved On</label>
                  <div class="date-display">
                    {{ formatDateDisplay(caregiverSchedule.approved_at || caregiverSchedule.reviewed_at) }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Schedule Grid (read-only) -->
            <div class="schedule-calendar-grid view-only">
              <!-- Day headers (Sun through Sat) -->
              <div
                v-for="dayConfig in dayConfigs"
                :key="dayConfig.key + '-header'"
                class="schedule-day-header"
              >
                <span>{{ dayConfig.short }}</span>
              </div>

              <!-- Total Hours Header -->
              <div class="schedule-day-header weekly-summary-header">
                <span>Total Hours</span>
              </div>

              <!-- Day cells (read-only) -->
              <div
                v-for="dayConfig in dayConfigs"
                :key="dayConfig.key + '-cell'"
                class="schedule-day-cell"
              >
                <div
                  class="day-content"
                  :class="{
                    'day-enabled':
                      caregiverSchedule[dayConfig.key + '_enabled'],
                  }"
                >
                  <div
                    v-if="caregiverSchedule[dayConfig.key + '_enabled']"
                    class="multiple-shifts-display"
                  >
                    <div
                      v-for="(shift, shiftIndex) in getShiftsForDay(
                        dayConfig.key,
                      )"
                      :key="`${dayConfig.key}-shift-${shiftIndex}`"
                      class="shift-display-row"
                    >
                      <div class="shift-times-display">
                        <div class="time-display">
                          {{ formatTime(shift.start) }}
                        </div>
                        <span class="time-separator">-</span>
                        <div class="time-display">
                          {{ formatTime(shift.end) }}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else class="day-disabled-text">Off</div>
                </div>
              </div>

              <!-- Total Hours Cell (read-only) -->
              <div class="schedule-day-cell weekly-summary-cell">
                <div class="day-content">
                  <div class="weekly-summary-content">
                    <div class="summary-total">{{ weeklyScheduledTotal }}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Notes Display (read-only) -->
            <div class="notes-section mt-4" v-if="caregiverSchedule.notes">
              <label class="time-label">Schedule Notes</label>
              <div class="notes-display">{{ caregiverSchedule.notes }}</div>
            </div>
            <div class="notes-section mt-4" v-if="caregiverSchedule.is_upcoming && caregiverSchedule.reason">
              <label class="time-label">Reason for Change</label>
              <div class="notes-display">{{ caregiverSchedule.reason }}</div>
            </div>
          </div>

          <!-- Upcoming Approved Schedule (read-only) -->
          <div v-if="scheduleViewMode === 'upcoming' && approvedScheduleChangeRequest">
            <div class="schedule-date-range view-only">
              <div class="date-range-grid">
                <div class="date-input-group">
                  <label class="time-label">Effective Date</label>
                  <div class="date-display">
                    {{ formatDateDisplay(approvedScheduleChangeRequest.effective_date) }}
                  </div>
                </div>
              </div>
              <div class="date-range-grid mt-2" v-if="approvedScheduleChangeRequest.reviewer">
                <div class="date-input-group">
                  <label class="time-label">Approved By</label>
                  <div class="date-display">
                    {{ approvedScheduleChangeRequest.reviewer.first_name }} {{ approvedScheduleChangeRequest.reviewer.last_name }}
                  </div>
                </div>
                <div class="date-input-group">
                  <label class="time-label">Approved On</label>
                  <div class="date-display">
                    {{ formatDateDisplay(approvedScheduleChangeRequest.reviewed_at) }}
                  </div>
                </div>
              </div>
            </div>

            <div class="schedule-calendar-grid view-only">
              <div
                v-for="dayConfig in dayConfigs"
                :key="dayConfig.key + '-upcoming-header'"
                class="schedule-day-header"
              >
                <span>{{ dayConfig.short }}</span>
              </div>
              <div class="schedule-day-header weekly-summary-header">
                <span>Total Hours</span>
              </div>

              <div
                v-for="dayConfig in dayConfigs"
                :key="dayConfig.key + '-upcoming-cell'"
                class="schedule-day-cell"
              >
                <div
                  class="day-content"
                  :class="{ 'day-enabled': getUpcomingShifts(dayConfig.key).length > 0 }"
                >
                  <div
                    v-if="getUpcomingShifts(dayConfig.key).length > 0"
                    class="multiple-shifts-display"
                  >
                    <div
                      v-for="(shift, shiftIndex) in getUpcomingShifts(dayConfig.key)"
                      :key="`${dayConfig.key}-upcoming-shift-${shiftIndex}`"
                      class="shift-display-row"
                    >
                      <div class="shift-times-display">
                        <div class="time-display">{{ formatTime(shift.start) }}</div>
                        <span class="time-separator">-</span>
                        <div class="time-display">{{ formatTime(shift.end) }}</div>
                      </div>
                    </div>
                  </div>
                  <div v-else class="day-disabled-text">Off</div>
                </div>
              </div>

              <div class="schedule-day-cell weekly-summary-cell">
                <div class="day-content">
                  <div class="weekly-summary-content">
                    <div class="summary-total">{{ upcomingWeeklyTotal }}</div>
                  </div>
                </div>
              </div>
            </div>

            <div class="notes-section mt-4" v-if="approvedScheduleChangeRequest.reason">
              <label class="time-label">Reason for Change</label>
              <div class="notes-display">{{ approvedScheduleChangeRequest.reason }}</div>
            </div>
          </div>

          <!-- Requested Schedule Change Interface (editable) -->
          <div v-if="scheduleViewMode === 'requested' && isViewingOwnPage">
            <!-- Date Range Selection -->
            <div class="schedule-date-range">
              <div class="date-range-grid">
                <div class="date-input-group">
                  <label class="time-label"
                    >Start Date {{ hasCurrentSchedule ? '(Must be at least 14 days out)' : '' }}</label
                  >
                  <input
                    type="date"
                    v-model="requestedScheduleChange.start_date"
                    :min="minScheduleChangeDate"
                    class="date-input"
                  />
                </div>
                <div class="date-input-group">
                  <label class="time-label">End Date</label>
                  <div class="date-display">Ongoing</div>
                </div>
              </div>
            </div>

            <!-- Schedule Grid (editable) -->
            <div class="schedule-calendar-grid">
              <!-- Day headers with checkboxes row (Sun through Sat) -->
              <div
                v-for="dayConfig in dayConfigs"
                :key="dayConfig.key + '-header'"
                class="schedule-day-header"
              >
                <label class="header-checkbox-label">
                  <span>{{ dayConfig.short }}</span>
                  <input
                    type="checkbox"
                    :checked="
                      requestedScheduleChange[dayConfig.key + '_enabled']
                    "
                    @change="
                      onRequestedDayEnabledChange(
                        dayConfig.key,
                        $event.target.checked,
                      )
                    "
                  />
                  <span class="schedule-checkmark"></span>
                </label>
              </div>

              <!-- Total Hours Header -->
              <div class="schedule-day-header weekly-summary-header">
                <span>Total Hours</span>
              </div>

              <!-- Day cells with multiple shift support -->
              <div
                v-for="dayConfig in dayConfigs"
                :key="dayConfig.key + '-cell'"
                class="schedule-day-cell"
              >
                <div
                  class="day-content"
                  :class="{
                    'day-enabled':
                      requestedScheduleChange[dayConfig.key + '_enabled'],
                  }"
                >
                  <div
                    v-if="requestedScheduleChange[dayConfig.key + '_enabled']"
                    class="multiple-shifts-controls"
                  >
                    <div
                      v-for="(shift, shiftIndex) in getRequestedShiftsForDay(
                        dayConfig.key,
                      )"
                      :key="`${dayConfig.key}-shift-${shiftIndex}`"
                      class="shift-row"
                    >
                      <div
                        v-if="getRequestedShiftsForDay(dayConfig.key).length > 1"
                        class="shift-remove-row"
                      >
                        <button
                          @click="removeRequestedShift(dayConfig.key, shiftIndex)"
                          class="remove-shift-btn"
                          title="Remove shift"
                        >
                          <i class="fa fa-times"></i>
                        </button>
                      </div>
                      <div class="shift-times">
                        <div class="time-input-group">
                          <label class="time-label">Start</label>
                          <AIVTimeSelect
                            :value="extractTimeFromDateTime(shift.start)"
                            @input="
                              updateRequestedShiftTime(
                                dayConfig.key,
                                shiftIndex,
                                'start',
                                $event,
                              )
                            "
                            :picker-options="{
                              selectableRange: '00:00:00 - 23:59:59',
                              start: '00:00',
                              step: '00:15',
                              end: '23:59',
                              format: 'HH:mm',
                            }"
                            placeholder="Start Time"
                          />
                        </div>
                        <div class="time-input-group">
                          <label class="time-label">End</label>
                          <AIVTimeSelect
                            :value="extractTimeFromDateTime(shift.end)"
                            @input="
                              updateRequestedShiftTime(
                                dayConfig.key,
                                shiftIndex,
                                'end',
                                $event,
                              )
                            "
                            :picker-options="{
                              selectableRange: '00:00:00 - 23:59:59',
                              start: '00:00',
                              step: '00:15',
                              end: '23:59',
                              format: 'HH:mm',
                            }"
                            placeholder="End Time"
                          />
                        </div>
                      </div>
                    </div>
                    <button
                      @click="addRequestedShift(dayConfig.key)"
                      class="add-shift-btn"
                      title="Add another shift"
                    >
                      <i class="fa fa-plus"></i> Add Shift
                    </button>
                  </div>
                  <div v-else class="day-disabled-text">Off</div>
                </div>
              </div>

              <!-- Total Hours Cell (for requested schedule) -->
              <div class="schedule-day-cell weekly-summary-cell">
                <div class="day-content">
                  <div class="weekly-summary-content">
                    <div class="summary-total">{{ requestedScheduleWeeklyTotal }}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Reason for Change -->
            <div class="notes-section mt-4" v-if="hasCurrentSchedule">
              <label class="time-label">Reason for Change (Required)</label>
              <textarea
                v-model="requestedScheduleChange.reason"
                class="notes-textarea"
                rows="3"
                placeholder="Please explain why you need this schedule change..."
              ></textarea>
            </div>

            <!-- Submit Button -->
            <div class="schedule-submit-actions mt-4">
              <el-button
                type="primary"
                @click="submitScheduleChangeRequest"
                :disabled="!isRequestedScheduleValid"
              >
                <i class="fa fa-paper-plane"></i> Submit Schedule{{ hasCurrentSchedule ? ' Change' : '' }} Request
              </el-button>
              <el-button @click="scheduleViewMode = 'current'">
                Cancel
              </el-button>
            </div>
          </div>
        </div>
      </div>

      <!-- Training Tab -->
      <div v-if="activeTab === 'training' && canViewCompliance" class="caregiver-tab-pane">

        <!-- 1. Training Records & Certificates (primary content) -->
        <div class="training-section">
          <div class="d-flex align-items-center justify-content-between mb-3">
            <h5 class="mb-0">Training Records</h5>
            <button
              v-if="isAdmin || isLead"
              class="btn btn-sm btn-outline-primary"
              @click="showManualTrainingForm = !showManualTrainingForm"
            >
              <i :class="showManualTrainingForm ? 'fa fa-minus' : 'fa fa-plus'"></i>
              {{ showManualTrainingForm ? 'Cancel' : 'Add Hours' }}
            </button>
          </div>

          <!-- Manual Training Entry (collapsible) -->
          <div v-if="(isAdmin || isLead) && showManualTrainingForm" class="training-card mb-3">
            <div class="row">
              <div class="col-md-4 mb-2">
                <label>Date <span class="text-danger">*</span></label>
                <input
                  type="date"
                  class="form-control"
                  v-model="manualTraining.training_date"
                />
              </div>
              <div class="col-md-4 mb-2">
                <label>Training Type</label>
                <AIVSelect
                  v-model="manualTraining.training_type"
                  :options="trainingTypeOptions"
                  placeholder="Select type..."
                  :clearable="true"
                />
              </div>
              <div class="col-md-4 mb-2">
                <label>Hours <span class="text-danger">*</span></label>
                <input
                  type="number"
                  class="form-control"
                  v-model="manualTraining.hours_completed"
                  min="0.25"
                  step="0.25"
                  placeholder="e.g. 2.5"
                />
              </div>
            </div>
            <div class="row">
              <div class="col-md-6 mb-2">
                <label>Title</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="manualTraining.training_title"
                  placeholder="Training title"
                />
              </div>
              <div class="col-md-6 mb-2">
                <label>Supporting Document</label>
                <input
                  type="file"
                  class="form-control"
                  ref="manualTrainingFileInput"
                  @change="onManualTrainingFileChange"
                />
              </div>
            </div>
            <div class="row">
              <div class="col-12 mb-2">
                <label>Summary / Notes</label>
                <textarea
                  class="form-control"
                  v-model="manualTraining.summary"
                  rows="2"
                  placeholder="Brief description of training completed"
                ></textarea>
              </div>
            </div>
            <div class="text-right">
              <button
                class="btn btn-primary"
                :disabled="!canSubmitManualTraining || submittingManualTraining"
                @click="submitManualTraining"
              >
                <i v-if="submittingManualTraining" class="fa fa-spinner fa-spin"></i>
                {{ submittingManualTraining ? 'Saving...' : 'Save Training Record' }}
              </button>
            </div>
          </div>

          <!-- Training year selector and hours total -->
          <div class="training-hours-summary mb-3">
            <div class="row align-items-center">
              <div class="col-md-4">
                <label>Training Year</label>
                <AIVSelect
                  v-model="selectedTrainingYear"
                  :options="trainingYearOptions"
                  placeholder="Select Training Year"
                  @input="onTrainingYearChange"
                />
              </div>
              <div class="col-md-8">
                <div class="hours-total-card">
                  <div class="hours-total-label">Total Training Hours</div>
                  <div class="hours-total-value">
                    {{ trainingRecordsTotalHours }} hours
                    <span v-if="trainingYearStart && trainingYearEnd" class="training-year-range">
                      (Training year: {{ trainingYearStart }}&ndash;{{ trainingYearEnd }})
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="loadingTrainingRecords" class="text-center p-3">
            <i class="fa fa-spinner fa-spin"></i> Loading training records...
          </div>

          <div v-else-if="trainingCertificateRecords.length === 0" class="text-center p-3">
            <p class="text-muted">No training records found for this training year.</p>
          </div>

          <div v-else class="table-responsive">
            <table class="table table-hover">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Source</th>
                  <th>Training Type</th>
                  <th>Title</th>
                  <th>Hours</th>
                  <th>Summary</th>
                  <th>Certificate</th>
                  <th>Proof</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="record in trainingCertificateRecords" :key="record.id">
                  <td>{{ formatDateOnly(record.training_date) }}</td>
                  <td>
                    <span v-if="record.id_shifts" class="badge badge-secondary">Shift</span>
                    <span v-else class="badge badge-primary">Manual</span>
                  </td>
                  <td>{{ record.training_type || '-' }}</td>
                  <td>{{ record.training_title || '-' }}</td>
                  <td>
                    <span class="badge badge-info">{{ record.hours_completed }}h</span>
                  </td>
                  <td>
                    <span class="training-summary-preview" :title="record.summary">
                      {{ truncateSummary(record.summary) }}
                    </span>
                  </td>
                  <td>
                    <a
                      v-if="record.certificate_path"
                      :href="getCertificateUrl(record.id)"
                      target="_blank"
                      class="btn btn-sm btn-outline-primary"
                      title="Download Certificate"
                    >
                      <i class="fa fa-file-pdf"></i> PDF
                    </a>
                    <span v-else class="text-muted">-</span>
                  </td>
                  <td>
                    <a
                      v-if="record.external_proof_url"
                      :href="getProofUrl(record.id)"
                      target="_blank"
                      class="btn btn-sm btn-outline-secondary"
                      title="Download Proof"
                    >
                      <i class="fa fa-download"></i>
                    </a>
                    <label
                      v-else-if="isAdmin"
                      class="btn btn-sm btn-outline-secondary mb-0"
                      title="Upload Proof"
                    >
                      <i class="fa fa-upload"></i>
                      <input
                        type="file"
                        style="display:none"
                        @change="uploadTrainingProof(record.id, $event)"
                      />
                    </label>
                    <span v-else class="text-muted">-</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- 2. Client Training Approvals -->
        <div class="training-section">
          <h5>Client Training Approvals</h5>

          <div v-if="loadingTraining" class="text-center p-4">
            <i class="fa fa-spinner fa-spin"></i> Loading...
          </div>

          <div v-else-if="trainingRecords.length === 0" class="text-center p-4">
            <p class="text-muted">No pending or reviewed training submissions.</p>
          </div>

          <div v-else class="table-responsive">
            <table class="table table-hover">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th>Reviewed By</th>
                  <th>Reviewed</th>
                  <th v-if="isAdmin">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="record in trainingRecords" :key="record.id">
                  <td>
                    <strong>{{ record.client?.first_name }} {{ record.client?.last_name }}</strong>
                  </td>
                  <td>
                    <span
                      class="badge"
                      :class="{
                        'badge-warning': record.status === 'pending',
                        'badge-success': record.status === 'approved',
                        'badge-danger': record.status === 'rejected',
                      }"
                    >
                      {{ record.status.charAt(0).toUpperCase() + record.status.slice(1) }}
                    </span>
                  </td>
                  <td>{{ formatDate(record.submitted_at) }}</td>
                  <td>
                    <span v-if="record.approver">
                      {{ record.approver.first_name }} {{ record.approver.last_name }}
                    </span>
                    <span v-else class="text-muted">-</span>
                  </td>
                  <td>
                    <span v-if="record.approved_at">{{ formatDate(record.approved_at) }}</span>
                    <span v-else class="text-muted">-</span>
                  </td>
                  <td v-if="isAdmin">
                    <button
                      v-if="record.status === 'pending'"
                      class="btn btn-sm btn-success mr-1"
                      @click="approveTraining(record)"
                      title="Approve"
                    >
                      <i class="fa fa-check"></i>
                    </button>
                    <button
                      v-if="record.status === 'pending'"
                      class="btn btn-sm btn-danger"
                      @click="rejectTraining(record)"
                      title="Reject"
                    >
                      <i class="fa fa-times"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- 3. Training Documents & Links -->
        <div class="training-section">
          <div class="d-flex align-items-center justify-content-between mb-3">
            <h5 class="mb-0">Documents & Links</h5>
            <button
              class="btn btn-sm btn-outline-primary"
              @click="showDocumentUploadForm = !showDocumentUploadForm"
            >
              <i :class="showDocumentUploadForm ? 'fa fa-minus' : 'fa fa-plus'"></i>
              {{ showDocumentUploadForm ? 'Cancel' : 'Add Document' }}
            </button>
          </div>

          <!-- Upload / Add Link Form (collapsible) -->
          <div v-if="showDocumentUploadForm" class="training-card mb-3">
            <div class="row">
              <div class="col-md-4 mb-2">
                <label>Display Name</label>
                <el-input
                  v-model="newDocument.displayName"
                  placeholder="Enter display name"
                />
              </div>
              <div class="col-md-4 mb-2">
                <label>Type</label>
                <AIVSelect
                  v-model="newDocument.type"
                  :options="documentTypeOptions"
                  placeholder="Select type"
                />
              </div>
              <div class="col-md-4 mb-2">
                <label v-if="newDocument.type === 'file'">Select File</label>
                <label v-else>URL</label>
                <el-input
                  v-if="newDocument.type === 'link'"
                  v-model="newDocument.url"
                  placeholder="Enter URL"
                />
                <input
                  v-else
                  type="file"
                  ref="trainingFileInput"
                  @change="onTrainingFileChange"
                  class="form-control"
                />
              </div>
            </div>
            <div class="text-right">
              <el-button
                type="primary"
                @click="uploadTrainingDocument"
                :loading="uploadingDocument"
                :disabled="!canUpload"
              >
                {{ newDocument.type === "file" ? "Upload" : "Add Link" }}
              </el-button>
            </div>
          </div>

          <!-- Documents List -->
          <div v-if="loadingDocuments" class="text-center p-3">
            <i class="fa fa-spinner fa-spin"></i> Loading documents...
          </div>

          <div v-else-if="trainingDocuments.length === 0" class="text-center p-3">
            <p class="text-muted">No training documents or links added yet.</p>
          </div>

          <div v-else class="documents-list">
            <div
              v-for="doc in trainingDocuments"
              :key="doc.id"
              class="document-item"
            >
              <div class="document-icon">
                <i :class="doc.is_link ? 'fa fa-link' : 'fa fa-file'"></i>
              </div>
              <div class="document-info">
                <a
                  href="#"
                  class="document-name"
                  @click.prevent="viewTrainingDocument(doc)"
                >
                  {{ doc.display_name || doc.link }}
                </a>
                <div class="document-meta">
                  <span v-if="doc.uploader">
                    {{ doc.uploader.first_name }} {{ doc.uploader.last_name }}
                  </span>
                  <span v-if="doc.createdAt"> - {{ formatDate(doc.createdAt) }}</span>
                </div>
              </div>
              <button
                v-if="isAdmin"
                class="btn btn-sm btn-danger document-delete"
                @click="deleteTrainingDocument(doc)"
                title="Delete"
              >
                <i class="fa fa-trash"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Details Tab -->
      <div v-if="activeTab === 'details'" class="caregiver-tab-pane">
        <div v-if="loadingCaregiver" class="text-center p-4">
          <i class="fa fa-spinner fa-spin"></i> Loading caregiver details...
        </div>
        <div v-else-if="caregiver" class="caregiver-details-content">
          <el-form
            label-position="top"
            label-width="120px"
            class="full-width-form"
          >
            <AIVRow
              :record.sync="caregiver"
              :fields="[
                {
                  label: 'First Name',
                  key: 'first_name',
                  disabled: !canEditDetails,
                },
                {
                  label: 'Last Name',
                  key: 'last_name',
                  disabled: !canEditDetails,
                },
                { label: 'Email', key: 'email', disabled: !canEditDetails },
              ]"
            />
            <div class="row mb-3 w-100 mx-0">
              <div class="col-md-6 px-2">
                <label class="d-block">Address1</label>
                <AIVAddressAutocomplete
                  :value="caregiver.address ? caregiver.address.address1 : ''"
                  :disabled="!canEditDetails"
                  @input="val => { if (caregiver.address) $set(caregiver.address, 'address1', val); }"
                  @place-selected="applyCaregiverPlace"
                />
              </div>
              <div class="col-md-6 px-2">
                <label class="d-block">Address2</label>
                <el-input
                  :value="caregiver.address ? caregiver.address.address2 : ''"
                  @input="val => { if (caregiver.address) $set(caregiver.address, 'address2', val); }"
                  placeholder="Address2"
                  class="w-100"
                  :disabled="!canEditDetails"
                />
              </div>
            </div>
            <div v-if="caregiver.address" class="mb-2" style="margin-left: 8px;">
              <el-tag v-if="caregiver.address.geocode && caregiver.address.geocode.trim()" size="mini" type="success" effect="plain">Geocoded</el-tag>
              <template v-else>
                <el-tag size="mini" type="warning" effect="plain">Not Geocoded</el-tag>
                <el-button type="text" size="mini" style="margin-left: 6px; color: #6ba6cd;" @click="openCaregiverMapPinPicker">
                  <i class="fa-solid fa-map-location-dot"></i> Set on Map
                </el-button>
              </template>
            </div>
            <AIVMapPinPicker
              :visible.sync="showMapPinPicker"
              :geocode="mapPinPickerGeocode"
              :address="mapPinPickerAddress"
              @location-selected="handleCaregiverMapPinSelected"
            />
            <AIVRow
              :record.sync="caregiver.address"
              :fields="[
                { label: 'City', key: 'city', disabled: !canEditDetails },
                { label: 'State', key: 'state', disabled: !canEditDetails },
                { label: 'Zip', key: 'zip', disabled: !canEditDetails },
              ]"
            />
            <AIVRow
              :record.sync="caregiver"
              :fields="[
                {
                  label: 'Mobile Phone',
                  key: 'mobile_phone',
                  disabled: !canEditDetails,
                },
                {
                  label: 'Other Phone',
                  key: 'other_phone',
                  disabled: !canEditDetails,
                },
              ]"
            />
            <AIVRow
              :record.sync="caregiver"
              :fields="[
                {
                  label: 'Hire Date',
                  key: 'hire_date',
                  type: 'date',
                  disabled: !canEditAdminFields,
                },
              ]"
            />
            <AIVRow
              :record.sync="caregiver"
              :fields="[
                { label: 'Title', key: 'title', disabled: !canEditAdminFields },
                {
                  label: 'Business Unit',
                  key: 'business_unit',
                  type: 'select',
                  options: businessUnitOptions,
                  placeholder: 'Select Business Unit',
                  disabled: !canEditAdminFields,
                },
                {
                  label: 'Position ID',
                  key: 'position_id',
                  disabled: !canEditAdminFields,
                },
              ]"
            />
            <AIVRow
              :record.sync="caregiver"
              :fields="
                [
                  {
                    label: 'User Type',
                    key: 'user_type',
                    type: 'select',
                    options: userTypeOptions,
                    disabled: !canEditAdminFields,
                  },
                  caregiver.user_type === 'Caregiver' ||
                  caregiver.user_type === 'Employment Specialist'
                    ? {
                        label:
                          caregiver.user_type === 'Employment Specialist'
                            ? 'Manager'
                            : 'Lead',
                        key: 'id_lead',
                        type: 'select',
                        options: leadOptions,
                        placeholder:
                          caregiver.user_type === 'Employment Specialist'
                            ? 'Select Manager'
                            : 'Select Lead',
                        disabled: !canEditAdminFields,
                      }
                    : null,
                  {
                    label: 'Admin',
                    key: 'id_admin',
                    type: 'select',
                    options: adminOptions,
                    placeholder: 'Select Admin',
                    disabled: !canEditAdminFields,
                  },
                ].filter(Boolean)
              "
            />
            <div v-if="canEditAdminFields" class="row mb-3 w-100 mx-0">
              <div class="col-md-12 px-2">
                <label class="d-block">Bypass location check on clock-in</label>
                <el-switch
                  v-model="caregiver.bypass_geofence"
                  active-text="On"
                  inactive-text="Off"
                />
                <div class="text-muted mt-1" style="font-size: 0.8rem;">
                  When on, this caregiver skips the GPS distance check on every clock-in. Use for caregivers whose work spans multiple sites that aren't on the client record.
                </div>
              </div>
            </div>
            <div v-if="canEditDetails" class="text-center mt-4">
              <el-button type="primary" round @click="saveCaregiverDetails">
                Save Changes
              </el-button>
            </div>
          </el-form>
        </div>
        <div v-else class="text-center p-4">
          <p>No caregiver details available</p>
        </div>
      </div>

      <!-- Compliance Tab -->
      <div v-if="activeTab === 'compliance' && canViewCompliance" class="caregiver-tab-pane">
        <div v-if="loadingCompliance" class="compliance-loading">
          <i class="fas fa-spinner fa-spin fa-2x"></i>
          <span>Loading compliance data...</span>
        </div>
        <div v-else-if="complianceProfile" class="compliance-content">
          <!-- Overall Status -->
          <div class="compliance-header mb-4">
            <h5>
              Compliance Status:
              <span :style="{ color: complianceStatusColor() }">
                <i :class="complianceStatusIcon()"></i>
                {{ complianceProfile.overallStatus.toUpperCase() }}
              </span>
            </h5>
            <p v-if="complianceProfile.hireDate" style="color: #999;">
              Hire Date: {{ complianceProfile.hireDate }}
            </p>
          </div>

          <!-- Training Hours Progress -->
          <div class="training-hours-section mb-4 p-3" style="background: rgba(255,255,255,0.05); border-radius: 8px;">
            <h6>Annual Training Hours</h6>
            <el-progress
              :percentage="Math.min(100, Math.round((complianceProfile.trainingHours.totalHours / complianceProfile.trainingHours.required) * 100))"
              :color="complianceProfile.trainingHours.totalHours >= complianceProfile.trainingHours.required ? '#67c23a' : '#e6a23c'"
            />
            <p class="mt-1" style="font-size: 13px; color: #ccc;">
              {{ complianceProfile.trainingHours.totalHours }} / {{ complianceProfile.trainingHours.required }} hours
              <span v-if="complianceProfile.trainingHours.isFirstYear"> (First year - exempt)</span>
              <span v-else-if="complianceProfile.trainingHours.daysUntilAnniversary != null">
                &mdash; {{ complianceProfile.trainingHours.daysUntilAnniversary }} days until anniversary
              </span>
            </p>
            <el-button v-if="complianceProfile.trainingHours && !complianceProfile.trainingHours.isFirstYear" type="text" size="small" @click="showTrainingHistory = true" style="padding: 0;">
              <i class="fa fa-history"></i> View History
            </el-button>
          </div>

          <!-- Training History Modal -->
          <AIVDialog
            v-if="showTrainingHistory"
            title="Training History (Archive)"
            :visible.sync="showTrainingHistory"
            width="600px"
          >
            <div class="p-3">
              <div v-if="loadingTrainingHistory" class="text-center p-3">
                <i class="fa fa-spinner fa-spin"></i> Loading...
              </div>
              <div v-else-if="trainingHistoryRecords.length === 0" class="text-center p-3">
                <p style="color: #999;">No archived training periods yet.</p>
              </div>
              <div v-else>
                <div v-for="record in trainingHistoryRecords" :key="record.id" class="p-2 mb-2" style="border-bottom: 1px solid rgba(255,255,255,0.1);">
                  <div class="d-flex justify-content-between">
                    <strong>{{ record.period_start }} &mdash; {{ record.period_end }}</strong>
                    <span :style="{ color: record.met_requirement ? '#67c23a' : '#f56c6c' }">
                      {{ record.total_hours }} / {{ record.required_hours }} hrs
                      <i :class="record.met_requirement ? 'fa fa-check-circle' : 'fa fa-times-circle'"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </AIVDialog>

          <!-- Document Breakdown by Tier -->
          <div v-for="item in complianceProfile.breakdown" :key="item.documentType" class="compliance-doc-row mb-2 p-2" style="display: flex; align-items: center; gap: 12px; border-bottom: 1px solid rgba(255,255,255,0.1);">
            <span :style="{ color: (item.tier === 4 && !item.isRequired && (item.status === 'missing' || item.status === 'rejected')) ? '#909399' : docStatusColor(item.status), fontSize: '18px' }">
              <i :class="(item.tier === 4 && !item.isRequired && item.status === 'missing') ? 'fa fa-minus-circle' : docStatusIcon(item.status)"></i>
            </span>
            <div style="flex: 1;">
              <strong>{{ item.documentType }}</strong>
              <span style="color: #999; font-size: 12px;"> ({{ item.tierLabel }})</span>
              <span v-if="item.tier === 4 && !item.isRequired" style="color: #909399; font-size: 11px; margin-left: 4px;">(only for ABI clients)</span>
              <div v-if="item.documentType === 'ABI/TBI Certification' && item.status === 'valid'" style="font-size: 12px; color: #67c23a;">
                Permanent (no expiration)
              </div>
              <div v-else-if="item.detail && item.detail.expirationDate" style="font-size: 12px; color: #aaa;">
                Expires: {{ item.detail.expirationDate }}
                <span v-if="item.daysUntilExpiration != null && item.daysUntilExpiration > 0"> ({{ item.daysUntilExpiration }} days)</span>
                <span v-else-if="item.daysUntilExpiration != null && item.daysUntilExpiration <= 0" style="color: #f44336;"> (EXPIRED)</span>
              </div>
              <div v-if="item.status === 'waivered' && item.detail" style="font-size: 12px; color: #FF9800;">
                Waivered until {{ item.detail.waiverExpirationDate }}
              </div>
              <div v-if="item.dueDate" style="font-size: 12px; color: #aaa;">
                Due by: {{ item.dueDate }}
                <span v-if="!item.isRequired" style="color: #67c23a;"> (not yet required)</span>
              </div>
              <div v-if="item.detail && item.detail.gracePeriodStart" style="font-size: 12px; color: #FF9800;">
                Grace period since {{ item.detail.gracePeriodStart }}
              </div>
            </div>
            <span class="compliance-status-badge" :style="{ background: (item.tier === 4 && !item.isRequired && item.status === 'missing') ? '#606060' : docStatusBadgeBg(item.status), color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', textTransform: 'uppercase' }">
              {{ (item.tier === 4 && !item.isRequired && item.status === 'missing') ? 'not needed' : item.status.replace('_', ' ') }}
            </span>
            <span v-if="item.detail && item.detail.id && !item.detail.filePath" style="font-size: 11px; color: #909399; margin-right: 8px;" title="Imported from ADP — no document file attached yet">Imported (date only)</span>
            <el-button v-if="item.detail && item.detail.id && item.detail.filePath" size="mini" icon="el-icon-view" @click="viewCompDoc(item.detail.id)" title="View Document">View</el-button>
            <el-button v-if="item.detail && item.detail.id && item.detail.filePath" size="mini" icon="el-icon-download" @click="downloadCompDoc(item.detail.id, item.documentType)" title="Download Document">Download</el-button>
            <el-button v-if="item.status === 'waivered' && item.detail && item.detail.id" size="mini" icon="el-icon-document" @click="viewCompWaiver(item.detail.id)" title="View Waiver">Waiver</el-button>
            <el-button v-if="canEditAdminFields && item.detail && item.detail.id" size="mini" icon="el-icon-edit" @click="editCompDoc(item)" title="Edit">Edit</el-button>
            <el-button v-if="canEditAdminFields && item.detail && item.detail.id" size="mini" type="danger" icon="el-icon-delete" @click="deleteCompDoc(item.detail.id, item.documentType)" title="Delete">Delete</el-button>
            <el-button
              v-if="isViewingOwnPage && !(item.tier === 4 && !item.isRequired)"
              size="mini"
              icon="el-icon-upload2"
              :loading="selfUploadingDocType === item.documentType"
              @click="$refs['selfUpload_' + item.documentType][0].click()"
              title="Upload Document"
            >Upload</el-button>
            <input
              v-if="isViewingOwnPage && !(item.tier === 4 && !item.isRequired)"
              type="file"
              :ref="'selfUpload_' + item.documentType"
              accept="image/*,.pdf"
              style="display: none;"
              @change="selfUploadComplianceDoc(item.documentType, $event)"
            />
          </div>

          <!-- Edit Compliance Document Modal -->
          <AIVDialog
            v-if="showEditCompDocModal"
            title="Edit Compliance Document"
            :visible.sync="showEditCompDocModal"
            width="500px"
          >
            <div class="p-3">
              <div class="mb-3">
                <label class="form-label">Document Type</label>
                <el-input :value="editingCompDoc.documentType" disabled />
              </div>
              <div class="mb-3">
                <label class="form-label">Expiration Date</label>
                <el-date-picker
                  v-model="editingCompDoc.expirationDate"
                  type="date"
                  placeholder="MM/DD/YYYY"
                  value-format="yyyy-MM-dd"
                  format="MM/dd/yyyy"
                  :editable="true"
                  style="width: 100%;"
                />
              </div>
              <div class="mb-3">
                <label class="form-label">Status</label>
                <el-select v-model="editingCompDoc.status" style="width: 100%;">
                  <el-option label="Pending" value="Pending" />
                  <el-option label="Approved" value="Approved" />
                  <el-option label="Expired" value="Expired" />
                  <el-option label="Rejected" value="Rejected" />
                </el-select>
              </div>
            </div>
            <template #footer>
              <el-button @click="showEditCompDocModal = false">Cancel</el-button>
              <el-button type="primary" @click="saveEditCompDoc" :loading="savingCompDoc">Save</el-button>
            </template>
          </AIVDialog>

          <!-- Upload Document Form (Admin) -->
          <div v-if="canEditAdminFields" class="mt-4 p-3" style="background: rgba(255,255,255,0.05); border-radius: 8px;">
            <h6>Upload Compliance Document</h6>
            <div class="row">
              <div class="col-md-3 mb-2">
                <label class="form-label">Document Type</label>
                <el-select v-model="compUploadType" placeholder="Select type" style="width: 100%;">
                  <el-option label="Drivers License" value="Drivers License" />
                  <el-option label="Auto Insurance" value="Auto Insurance" />
                  <el-option label="Auto Registration" value="Auto Registration" />
                  <el-option label="CPR" value="CPR" />
                  <el-option label="SOAR" value="SOAR" />
                  <el-option label="ABI/TBI Certification" value="ABI/TBI Certification" />
                </el-select>
              </div>
              <div class="col-md-3 mb-2">
                <label class="form-label">Expiration Date</label>
                <el-date-picker v-model="compUploadExpiration" type="date" placeholder="MM/DD/YYYY" size="small" value-format="yyyy-MM-dd" format="MM/dd/yyyy" :editable="true" style="width: 100%;" />
              </div>
              <div class="col-md-3 mb-2">
                <label class="form-label">File</label>
                <div>
                  <input type="file" ref="compFileInput" accept="image/*,.pdf" style="display: none;" @change="compFileName = $event.target.files[0] ? $event.target.files[0].name : ''" />
                  <el-button size="small" icon="el-icon-folder-opened" @click="$refs.compFileInput.click()">
                    {{ compFileName || 'Choose File' }}
                  </el-button>
                </div>
              </div>
              <div class="col-md-3 mb-2">
                <label class="form-label">&nbsp;</label>
                <el-button type="primary" size="small" @click="uploadComplianceDoc" :loading="uploadingCompDoc" style="display: block;">
                  <i class="el-icon-upload2" style="margin-right: 4px;"></i> Upload
                </el-button>
              </div>
            </div>
          </div>

          <!-- Pending Approvals (Admin) -->
          <div v-if="canEditAdminFields && pendingCompDocs.length > 0" class="mt-4">
            <h6>Pending Approvals</h6>
            <div v-for="doc in pendingCompDocs" :key="doc.id" class="pending-doc-row p-2 mb-2" style="border: 1px solid rgba(255,193,7,0.3); border-radius: 6px;">
              <div class="d-flex align-items-center justify-content-between">
                <div>
                  <strong>{{ doc.document_type }}</strong>
                  <span v-if="doc.ocr_expiration_date" style="color: #67c23a; font-size: 12px;"> Suggested date: {{ doc.ocr_expiration_date }}</span>
                </div>
                <div>
                  <el-button size="mini" icon="el-icon-view" @click="viewCompDoc(doc.id)" title="View Document">View</el-button>
                  <el-date-picker v-model="doc._approveDate" type="date" placeholder="MM/DD/YYYY" size="mini" value-format="yyyy-MM-dd" format="MM/dd/yyyy" :editable="true" class="mr-2" style="width: 160px;" />
                  <el-button size="mini" type="success" @click="approveCompDoc(doc)">Approve</el-button>
                  <el-button size="mini" type="danger" @click="rejectCompDoc(doc)">Reject</el-button>
                  <el-button size="mini" @click="grantCompWaiver(doc)">Waiver</el-button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="text-center p-4">
          <p>No compliance data available</p>
        </div>
      </div>
    </div>

    <!-- Client Access Tab (Admin only) -->
    <div v-if="activeTab === 'client-access' && isAdmin" class="caregiver-tab-pane">
      <div class="client-access-section">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5 class="mb-0">Client Access</h5>
          <button class="btn btn-sm btn-primary" @click="showGrantClientAccessModal = true">
            <i class="fa fa-plus"></i> Grant Access
          </button>
        </div>

        <p class="text-muted small mb-3">
          Clients this caregiver can view. Access is automatically granted when assigned to shifts or schedules, but can also be managed manually here.
        </p>

        <div v-if="loadingClientAccess" class="text-center p-3">
          <i class="fa fa-spinner fa-spin"></i> Loading...
        </div>

        <div v-else-if="clientAccessRecords.length === 0" class="text-center p-3">
          <p class="text-muted">No client access records. Grant access to allow this user to view client data.</p>
        </div>

        <div v-else class="table-responsive">
          <table class="table table-hover">
            <thead>
              <tr>
                <th>Client</th>
                <th>Payer Type</th>
                <th>Source</th>
                <th>Granted</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="record in clientAccessRecords" :key="record.id">
                <td>
                  <span v-if="record.client">{{ record.client.first_name }} {{ record.client.last_name }}</span>
                  <span v-else class="text-muted">Unknown</span>
                </td>
                <td>{{ record.client ? record.client.payer_type : '-' }}</td>
                <td>
                  <span class="badge" :class="{
                    'badge-secondary': record.source === 'shift',
                    'badge-info': record.source === 'schedule',
                    'badge-primary': record.source === 'manual',
                    'badge-warning': record.source === 'lead',
                  }">{{ record.source }}</span>
                </td>
                <td>{{ formatDateShort(record.createdAt) }}</td>
                <td>
                  <button
                    class="btn btn-sm btn-outline-danger"
                    @click="revokeClientAccess(record)"
                    title="Revoke access"
                  >
                    <i class="fa fa-times"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Grant Access Modal -->
      <AIVDialog
        v-if="showGrantClientAccessModal"
        title="Grant Client Access"
        :visible.sync="showGrantClientAccessModal"
        width="500px"
      >
        <div class="p-3">
          <div v-if="loadingAvailableClients" class="text-center p-3">
            <i class="fa fa-spinner fa-spin"></i> Loading clients...
          </div>
          <div v-else>
            <label>Select clients to grant access</label>
            <div class="available-clients-list" style="max-height: 400px; overflow-y: auto;">
              <div v-for="client in availableClientsForAccess" :key="client.id" class="form-check mb-1">
                <input
                  type="checkbox"
                  class="form-check-input"
                  :id="'grant-client-' + client.id"
                  :value="client.id"
                  v-model="selectedClientsToGrant"
                />
                <label class="form-check-label" :for="'grant-client-' + client.id">
                  {{ client.first_name }} {{ client.last_name }}
                  <small class="text-muted">({{ client.payer_type || 'N/A' }})</small>
                </label>
              </div>
              <p v-if="availableClientsForAccess.length === 0" class="text-muted">
                This user already has access to all active clients.
              </p>
            </div>
          </div>
        </div>
        <template #footer>
          <button class="btn btn-default" @click="showGrantClientAccessModal = false">Cancel</button>
          <button
            class="btn btn-primary"
            :disabled="selectedClientsToGrant.length === 0"
            @click="grantClientAccessBulk"
          >
            Grant Access ({{ selectedClientsToGrant.length }})
          </button>
        </template>
      </AIVDialog>
    </div>

    <!-- Shift Modal -->
    <ShiftModal
      :visible.sync="showShiftModal"
      :shift-being-edited="shiftBeingEdited"
      :available-clients="availableClients"
      :available-caregivers="[{ id: Number(caregiverId), name: caregiverName }]"
      :allow-delete="true"
      @cancel="closeShiftModal"
      @save="saveShift"
      @delete="handleEditModalDelete"
    />

    <!-- Shift Action Modal -->
    <ShiftActionModal
      :visible.sync="showShiftActionModal"
      :shift="selectedShiftForActions"
      :view-only="isViewOnlyMode"
      hideField="caregiver"
      @edit-shift="editShift"
      @edit-shift-notes="handleEditShiftNotes"
      @delete-shift="handleShiftDeletion"
      @clock-in="handleClockIn"
      @clock-out="handleClockOut"
      @admin-clock-out="handleAdminClockOut"
      @edit-clock-in-time="handleEditClockInTime"
      @edit-clock-out-time="handleEditClockOutTime"
      @approve-shift="handleApproveShift"
      @undo-approval="handleUndoApproval"
      @approve-missed-shift="handleApproveMissedShift"
      @cancel-shift="handleCancelShift"
      @open-tasks="handleOpenGroupTasks"
    />

    <!-- Delete Recurring Shift Dialog -->
    <DeleteRecurringShiftDialog
      :visible.sync="deleteRecurringDialog.visible"
      @this-only="deleteThisShiftOnly"
      @this-and-future="deleteThisAndFutureShifts"
      @all-shifts="deleteAllShifts"
    />

    <GroupShiftTasksModal
      :visible.sync="showGroupTasksModal"
      :shift="groupTasksShift"
      :user-id="caregiverId"
      @refresh="loadCaregiverShifts"
    />

    <!-- Clock In Modal -->
    <ClockInModal
      :visible.sync="showClockInModal"
      :shift="shiftToClockIn"
      :user-id="caregiverId"
      @clock-in-success="handleClockInSuccess"
    />

    <!-- Clock Out Modal -->
    <ClockOutModal
      :visible.sync="showClockOutModal"
      :shift="shiftToClockOut"
      :user-id="caregiverId"
      @clock-out-success="handleClockOutSuccess"
    />

    <!-- Admin Clock Out Modal -->
    <AdminClockOutModal
      :visible.sync="showAdminClockOutModal"
      :shift="shiftToAdminClockOut"
      @clocked-out="loadCaregiverShifts"
    />

    <!-- Edit Clock In Time Modal -->
    <EditClockInTimeModal
      :visible.sync="showEditClockInTimeModal"
      :shift="shiftToEditClockInTime"
      @clock-in-time-updated="loadCaregiverShifts"
    />

    <!-- Edit Clock Out Time Modal -->
    <EditClockOutTimeModal
      :visible.sync="showEditClockOutTimeModal"
      :shift="shiftToEditClockOutTime"
      @clock-out-time-updated="loadCaregiverShifts"
    />

    <!-- Edit Shift Notes Modal -->
    <EditShiftNotesModal
      :visible.sync="showEditShiftNotesModal"
      :shift="shiftToEditNotes"
      @updated="loadCaregiverShifts"
    />

    <!-- Approve Shift Modal -->
    <ApproveShiftModal
      :visible.sync="showApproveShiftModal"
      :shift="shiftToApprove"
      @shift-approved="loadCaregiverShifts"
    />

    <!-- Approve Missed Shift Modal -->
    <ApproveMissedShift
      :visible.sync="showApproveMissedShiftModal"
      :shift="shiftToApproveMissed"
      @shift-approved="loadCaregiverShifts"
    />

    <!-- Password Change Modal -->
    <ChangePasswordModal
      v-model="showPasswordChangeModal"
      :isForced="false"
      @success="handlePasswordChangeSuccess"
    />
  </div>
</template>

<script>
import { caregiversService } from "@/serviceHandlers/caregiversService";
import { milesForCaregiver } from "@/utils/shiftMiles";
import { shiftsService } from "@/serviceHandlers/shiftsService";
import { schedulesService } from "@/serviceHandlers/schedulesService";
import { caregiverTrainingService } from "@/serviceHandlers/caregiverTrainingService";
import { trainingRecordsService } from "@/serviceHandlers/trainingRecordsService";
import { userClientAccessService } from "@/serviceHandlers/userClientAccessService";
import { documentsService } from "@/serviceHandlers/documentsService";
import { scheduleChangeRequestsService } from "@/serviceHandlers/scheduleChangeRequestsService";
import { clientsService } from "@/serviceHandlers/clientsService";
import { caregiverComplianceService } from "@/serviceHandlers/caregiverComplianceService";
import MonthView from "@/pages/Calendar/MonthView.vue";
import AIVTimeSelect from "@/components/AIVTimeSelect.vue";
import AIVSelect from "@/components/AIVSelect.vue";
import AIVRow from "@/components/AIVRow.vue";
import { useLoginStore } from "@/stores/loginStore";
import { isDSPDGroupShift, getCaregiverShiftStatus } from "@/utils/shiftStatus";
import ShiftModal from "@/components/ShiftModal.vue";
import ShiftActionModal from "@/components/ShiftActionModal.vue";
import DeleteRecurringShiftDialog from "@/components/DeleteRecurringShiftDialog.vue";
import ClockInModal from "@/components/ClockInModal.vue";
import ClockOutModal from "@/components/ClockOutModal.vue";
import AdminClockOutModal from "@/components/AdminClockOutModal.vue";
import EditClockInTimeModal from "@/components/EditClockInTimeModal.vue";
import EditClockOutTimeModal from "@/components/EditClockOutTimeModal.vue";
import EditShiftNotesModal from "@/components/EditShiftNotesModal.vue";
import ApproveShiftModal from "@/components/ApproveShiftModal.vue";
import ApproveMissedShift from "@/components/ApproveMissedShift.vue";
import GroupShiftTasksModal from "@/components/GroupShiftTasksModal.vue";
import ChangePasswordModal from "@/components/ChangePasswordModal.vue";
import { timezoneService } from "@/serviceHandlers/timezoneService";
import AIVAddressAutocomplete from "@/components/AIVAddressAutocomplete.vue";
import AIVMapPinPicker from "@/components/AIVMapPinPicker.vue";
import {
  TIMEZONE_OPTIONS,
  getBrowserTimezone,
  formatTimezoneLabel,
} from "@/utils/timezoneConstants";
import conflictMessageMixin from "@/mixins/conflictMessageMixin";
import { parseDateOnly, localDateString } from "@/utils/dateOnly";

export default {
  name: "CaregiverDetails",
  mixins: [conflictMessageMixin],
  components: {
    MonthView,
    AIVTimeSelect,
    AIVSelect,
    AIVRow,
    ShiftModal,
    ShiftActionModal,
    DeleteRecurringShiftDialog,
    ClockInModal,
    ClockOutModal,
    AdminClockOutModal,
    EditClockInTimeModal,
    EditClockOutTimeModal,
    EditShiftNotesModal,
    ApproveShiftModal,
    ApproveMissedShift,
    GroupShiftTasksModal,
    ChangePasswordModal,
    AIVAddressAutocomplete,
    AIVMapPinPicker,
  },
  data() {
    return {
      showMapPinPicker: false,
      mapPinPickerGeocode: "",
      mapPinPickerAddress: {},
      activeTab: "calendar",
      caregiverId: this.$route.params.id || null,
      caregiverName: this.$route.query.name || "Caregiver",
      caregiverShifts: [],
      caregiverSchedules: [],
      caregiverSchedule: {},
      caregiverApprovedTimeOff: [],
      scheduleTimezone: null,
      inferredCaregiverTimezone: null,
      timezoneOptions: TIMEZONE_OPTIONS,
      selectedScheduleIndex: 0,
      loadingShifts: false,
      loadingSchedule: false,
      currentMonth: new Date(),
      daysInMonth: [],
      dayNames: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
      dayConfigs: [
        { key: "sunday", short: "Sun", full: "Sunday" },
        { key: "monday", short: "Mon", full: "Monday" },
        { key: "tuesday", short: "Tue", full: "Tuesday" },
        { key: "wednesday", short: "Wed", full: "Wednesday" },
        { key: "thursday", short: "Thu", full: "Thursday" },
        { key: "friday", short: "Fri", full: "Friday" },
        { key: "saturday", short: "Sat", full: "Saturday" },
      ],
      // Shift modals
      showShiftModal: false,
      shiftBeingEdited: null,
      showShiftActionModal: false,
      deleteRecurringDialog: { visible: false, shift: null },
      selectedShift: null,
      selectedShiftForActions: null,
      isViewOnlyMode: false,
      showClockInModal: false,
      shiftToClockIn: null,
      showClockOutModal: false,
      shiftToClockOut: null,
      showAdminClockOutModal: false,
      shiftToAdminClockOut: null,
      showEditClockInTimeModal: false,
      shiftToEditClockInTime: null,
      showEditClockOutTimeModal: false,
      shiftToEditClockOutTime: null,
      showEditShiftNotesModal: false,
      shiftToEditNotes: null,
      showApproveShiftModal: false,
      shiftToApprove: null,
      showApproveMissedShiftModal: false,
      shiftToApproveMissed: null,
      showGroupTasksModal: false,
      groupTasksShift: null,
      availableClients: [],
      // Schedule change requests
      pendingScheduleChangeRequests: [],
      loadingRequests: false,
      approvedScheduleChangeRequest: null,
      adminScheduleViewMode: "current",
      selectedPendingRequestIndex: 0,
      reviewSubmitting: false,
      showPasswordChangeModal: false,
      // Inline schedule change request
      scheduleViewMode: "current", // 'current' or 'requested'
      requestedScheduleChange: {},
      // Details tab data
      caregiver: null,
      loadingCaregiver: false,
      // Training tab data
      trainingRecords: [],
      loadingTraining: false,
      // Training documents/links
      trainingDocuments: [],
      loadingDocuments: false,
      uploadingDocument: false,
      newDocument: {
        displayName: "",
        type: "file",
        url: "",
        file: null,
      },
      documentTypeOptions: [
        { id: "file", name: "Upload File" },
        { id: "link", name: "Add Link" },
      ],
      // Training certificate records
      trainingCertificateRecords: [],
      trainingRecordsTotalHours: 0,
      trainingYearStart: "",
      trainingYearEnd: "",
      selectedTrainingYear: null,
      loadingTrainingRecords: false,
      // Manual training entry
      showManualTrainingForm: false,
      showDocumentUploadForm: false,
      submittingManualTraining: false,
      manualTraining: {
        training_date: "",
        training_type: "",
        training_title: "",
        hours_completed: "",
        summary: "",
      },
      manualTrainingFile: null,
      trainingTypeOptions: [
        { id: "In-Service", name: "In-Service" },
        { id: "Online Course", name: "Online Course" },
        { id: "Conference", name: "Conference" },
        { id: "Workshop", name: "Workshop" },
        { id: "Orientation", name: "Orientation" },
        { id: "CPR/First Aid", name: "CPR/First Aid" },
        { id: "Medication Management", name: "Medication Management" },
        { id: "Safety Training", name: "Safety Training" },
        { id: "Client-Specific", name: "Client-Specific" },
        { id: "Other", name: "Other" },
      ],
      userTypeOptions: [
        { id: "Lead", name: "Lead" },
        { id: "Caregiver", name: "Caregiver" },
        { id: "Employment Specialist", name: "Employment Specialist" },
      ],
      businessUnitOptions: [
        { id: "VRS - Voc Rehab", name: "VRS - Voc Rehab" },
        {
          id: "CBS - Community Based Services",
          name: "CBS - Community Based Services",
        },
        { id: "HBS - Home Based Services", name: "HBS - Home Based Services" },
        { id: "HH - Host Home Services", name: "HH - Host Home Services" },
      ],
      leadOptions: [],
      adminOptions: [],
      // Compliance tab data
      complianceProfile: null,
      loadingCompliance: false,
      pendingCompDocs: [],
      compUploadType: '',
      compUploadExpiration: '',
      uploadingCompDoc: false,
      selfUploadingDocType: null,
      compFileName: '',
      showEditCompDocModal: false,
      editingCompDoc: { id: null, documentType: '', expirationDate: '', status: '' },
      savingCompDoc: false,
      showTrainingHistory: false,
      loadingTrainingHistory: false,
      trainingHistoryRecords: [],
      // Client access tab data
      clientAccessRecords: [],
      loadingClientAccess: false,
      showGrantClientAccessModal: false,
      availableClientsForAccess: [],
      loadingAvailableClients: false,
      selectedClientsToGrant: [],
    };
  },
  computed: {
    browserTimezone() {
      return getBrowserTimezone();
    },
    showScheduleTimezoneWarning() {
      return (
        this.scheduleTimezone &&
        this.scheduleTimezone !== this.browserTimezone
      );
    },
    scheduleTimezoneConversionHint() {
      if (!this.showScheduleTimezoneWarning || !this.caregiverSchedule) return "";
      try {
        // Find the first enabled day with a shift to use as an example
        const days = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
        let exampleStart = null;
        let exampleEnd = null;
        for (const day of days) {
          if (this.caregiverSchedule[`${day}_enabled`] && this.caregiverSchedule[`${day}_shifts`]?.length > 0) {
            const shift = this.caregiverSchedule[`${day}_shifts`][0];
            if (shift.start && shift.end) {
              exampleStart = shift.start;
              exampleEnd = shift.end;
              break;
            }
          }
        }
        if (!exampleStart || !exampleEnd) return "Enter times based on when the schedule occurs in your timezone.";
        // Build Date objects from the example times (browser-local)
        const today = localDateString();
        const startLocal = new Date(`${today}T${exampleStart}:00`);
        const endLocal = new Date(`${today}T${exampleEnd}:00`);
        if (isNaN(startLocal.getTime()) || isNaN(endLocal.getTime())) return "";
        const fmt = { hour: "numeric", minute: "2-digit", hour12: true };
        const startBrowser = startLocal.toLocaleTimeString("en-US", { ...fmt, timeZone: this.browserTimezone });
        const endBrowser = endLocal.toLocaleTimeString("en-US", { ...fmt, timeZone: this.browserTimezone });
        const startSchedule = startLocal.toLocaleTimeString("en-US", { ...fmt, timeZone: this.scheduleTimezone });
        const endSchedule = endLocal.toLocaleTimeString("en-US", { ...fmt, timeZone: this.scheduleTimezone });
        return `For example, ${startBrowser} \u2013 ${endBrowser} ${this.shortTzName(this.browserTimezone)} is ${startSchedule} \u2013 ${endSchedule} ${this.shortTzName(this.scheduleTimezone)}. Enter times based on when the schedule occurs in your timezone.`;
      } catch { return ""; }
    },
    isAdmin() {
      const store = useLoginStore();
      return store.isAdmin;
    },
    isLead() {
      const store = useLoginStore();
      return store.isLead;
    },
    isEmploymentSpecialist() {
      const store = useLoginStore();
      return store.isEmploymentSpecialist;
    },
    isCaregiver() {
      const store = useLoginStore();
      const userType = store.user?.user_type;
      return userType === "Caregiver" || userType === "Employment Specialist";
    },
    userId() {
      const store = useLoginStore();
      return store.user?.id;
    },
    canUpload() {
      if (!this.newDocument.displayName) return false;
      if (this.newDocument.type === "file") return !!this.newDocument.file;
      if (this.newDocument.type === "link") return !!this.newDocument.url;
      return false;
    },
    canSubmitManualTraining() {
      return this.manualTraining.training_date && parseFloat(this.manualTraining.hours_completed) > 0;
    },
    trainingYearOptions() {
      if (!this.caregiver || !this.caregiver.hire_date) return [];
      // hire_date is DATE-only — parse locally, and keep the window ids in
      // local parts too (toISOString would shift both by a day).
      const hireDate = parseDateOnly(this.caregiver.hire_date);
      const today = new Date();
      const options = [];
      const fmt = (d) =>
        `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`;
      const toISO = (d) => localDateString(d);

      // Generate windows from hire date up through the current training year
      let start = new Date(hireDate);
      while (start <= today) {
        const end = new Date(start.getFullYear() + 1, start.getMonth(), start.getDate());
        options.push({
          id: `${toISO(start)}|${toISO(end)}`,
          name: `${fmt(start)}\u2013${fmt(end)}`,
        });
        start = end;
      }
      // Most recent training year first
      options.reverse();
      return options;
    },
    canEditDetails() {
      // Personal fields: Admins and Leads can edit anyone. Caregivers/Employment Specialists can only edit their own page.
      return (
        this.isAdmin ||
        this.isLead ||
        (this.isCaregiver && this.isViewingOwnPage)
      );
    },
    canEditAdminFields() {
      // Admin fields: Only Admins and Leads can edit. Caregivers/Employment Specialists cannot edit admin fields at all.
      return this.isAdmin || this.isLead;
    },
    canViewCompliance() {
      // Admins and Leads can view any caregiver's compliance. Caregivers can only view their own.
      return this.isAdmin || this.isLead || this.isViewingOwnPage;
    },
    canAddShift() {
      // Admins and Leads can add shifts to any caregiver calendar
      // Employment Specialists can only add shifts to their own calendar
      return (
        this.isAdmin ||
        this.isLead ||
        (this.isEmploymentSpecialist && this.isViewingOwnPage)
      );
    },
    currentMonthYear() {
      const month = this.currentMonth.toLocaleString("default", {
        month: "long",
      });
      const year = this.currentMonth.getFullYear();
      return `${month} ${year}`;
    },
    todayDate() {
      return localDateString();
    },
    hasCurrentSchedule() {
      return this.caregiverSchedules.some(s => !s.is_upcoming && s.id) || this.isApprovedScheduleCurrent;
    },
    isApprovedScheduleCurrent() {
      // Check if any approved schedule change (is_upcoming) is effective today or earlier
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return this.caregiverSchedules.some(s => {
        if (!s.is_upcoming) return false;
        const effective = this.parseDateLocal(s.start_date);
        effective.setHours(0, 0, 0, 0);
        return effective <= today;
      });
    },
    minScheduleChangeDate() {
      if (!this.hasCurrentSchedule) {
        return this.todayDate;
      }
      const date = new Date();
      date.setDate(date.getDate() + 14);
      return localDateString(date);
    },
    activeAndFutureSchedules() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const filtered = this.caregiverSchedules.filter((schedule) => {
        // Include is_upcoming (approved schedule changes) - they always have future or current dates
        if (schedule.is_upcoming) return true;
        const endDate = schedule.end_date ? this.parseDateLocal(schedule.end_date) : null;
        if (endDate) endDate.setHours(0, 0, 0, 0);
        return !endDate || endDate >= today;
      });

      // Sort so current schedule is first, then future schedules by start date
      return filtered.sort((a, b) => {
        const aStart = this.parseDateLocal(a.start_date);
        aStart.setHours(0, 0, 0, 0);
        const aEnd = a.end_date ? this.parseDateLocal(a.end_date) : null;
        if (aEnd) aEnd.setHours(0, 0, 0, 0);
        const bStart = this.parseDateLocal(b.start_date);
        bStart.setHours(0, 0, 0, 0);
        const bEnd = b.end_date ? this.parseDateLocal(b.end_date) : null;
        if (bEnd) bEnd.setHours(0, 0, 0, 0);

        const aIsCurrent = aStart <= today && (!aEnd || aEnd >= today);
        const bIsCurrent = bStart <= today && (!bEnd || bEnd >= today);

        // Current schedule comes first
        if (aIsCurrent && !bIsCurrent) return -1;
        if (!aIsCurrent && bIsCurrent) return 1;

        // Both current or both future: sort by start date
        return aStart - bStart;
      });
    },
    displayActiveSchedules() {
      // When an approved schedule change is current, filter out DB schedules
      // that overlap with today to avoid duplicate "Current" buttons
      if (!this.isApprovedScheduleCurrent) return this.activeAndFutureSchedules;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return this.activeAndFutureSchedules.filter(schedule => {
        // Always keep is_upcoming schedules
        if (schedule.is_upcoming) return true;
        // Filter out DB schedules that are currently active (superseded by approved change)
        const startDate = this.parseDateLocal(schedule.start_date);
        startDate.setHours(0, 0, 0, 0);
        const endDate = schedule.end_date ? this.parseDateLocal(schedule.end_date) : null;
        if (endDate) endDate.setHours(0, 0, 0, 0);
        return !(startDate <= today && (!endDate || endDate >= today));
      });
    },
    selectedPendingRequest() {
      return this.pendingScheduleChangeRequests[this.selectedPendingRequestIndex] || {};
    },
    requestedWeeklyTotal() {
      const request = this.selectedPendingRequest;
      if (!request || !request.requested_schedule) return '0h 0m';
      let totalMinutes = 0;
      this.dayConfigs.forEach(dayConfig => {
        const shifts = request.requested_schedule[dayConfig.key + '_shifts'] || [];
        shifts.forEach(shift => {
          if (shift.start && shift.end) {
            const [sh, sm] = shift.start.split(':').map(Number);
            const [eh, em] = shift.end.split(':').map(Number);
            let diff = (eh * 60 + em) - (sh * 60 + sm);
            if (diff < 0) diff += 24 * 60;
            totalMinutes += diff;
          }
        });
      });
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      return `${hours}h ${minutes}m`;
    },
    weeklyScheduledTotal() {
      let totalMinutes = 0;
      this.dayConfigs.forEach(dayConfig => {
        if (!this.caregiverSchedule[dayConfig.key + '_enabled']) return;
        const shifts = this.caregiverSchedule[dayConfig.key + '_shifts'] || [];
        shifts.forEach(shift => {
          if (shift.start && shift.end) {
            const [sh, sm] = shift.start.split(':').map(Number);
            const [eh, em] = shift.end.split(':').map(Number);
            let diff = (eh * 60 + em) - (sh * 60 + sm);
            if (diff < 0) diff += 24 * 60;
            totalMinutes += diff;
          }
        });
      });
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      return `${hours}h ${minutes}m`;
    },
    requestedScheduleWeeklyTotal() {
      let totalMinutes = 0;
      this.dayConfigs.forEach(dayConfig => {
        if (!this.requestedScheduleChange[dayConfig.key + '_enabled']) return;
        const shifts = this.requestedScheduleChange[dayConfig.key + '_shifts'] || [];
        shifts.forEach(shift => {
          if (shift.start && shift.end) {
            const [sh, sm] = shift.start.split(':').map(Number);
            const [eh, em] = shift.end.split(':').map(Number);
            let diff = (eh * 60 + em) - (sh * 60 + sm);
            if (diff < 0) diff += 24 * 60;
            totalMinutes += diff;
          }
        });
      });
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      return `${hours}h ${minutes}m`;
    },
    upcomingWeeklyTotal() {
      if (!this.approvedScheduleChangeRequest || !this.approvedScheduleChangeRequest.requested_schedule) return '0h 0m';
      const schedule = this.approvedScheduleChangeRequest.requested_schedule;
      return this.calculateWeeklyTotal(schedule);
    },
    pendingWeeklyTotal() {
      if (!this.pendingScheduleChangeRequests.length) return '0h 0m';
      const req = this.pendingScheduleChangeRequests[0];
      if (!req || !req.requested_schedule) return '0h 0m';
      return this.calculateWeeklyTotal(req.requested_schedule);
    },
    isViewingOwnPage() {
      const store = useLoginStore();
      const currentUserId = store.user?.id;
      return (
        currentUserId &&
        this.caregiverId &&
        String(currentUserId) === String(this.caregiverId)
      );
    },
    isRequestedScheduleValid() {
      const noStartDate = !this.requestedScheduleChange.start_date;
      if (noStartDate) {
        return false;
      }

      // Check date validity
      // Parse as local time to avoid UTC timezone shift
      const [y, m, d] = this.requestedScheduleChange.start_date.split('-').map(Number);
      const selectedDate = new Date(y, m - 1, d);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (this.hasCurrentSchedule) {
        // Must be at least 14 days out
        const minDate = new Date(today);
        minDate.setDate(minDate.getDate() + 14);
        if (selectedDate < minDate) {
          return false;
        }
      } else {
        // No current schedule - just needs to be today or future
        if (selectedDate < today) {
          return false;
        }
      }

      if (
        this.hasCurrentSchedule && (
        !this.requestedScheduleChange.reason ||
        this.requestedScheduleChange.reason.trim() === ""
        )
      ) {
        return false;
      }

      // Check if at least one day is enabled with valid shifts
      const hasEnabledDayWithValidShifts = this.dayConfigs.some((dayConfig) => {
        const enabledKey = `${dayConfig.key}_enabled`;
        const shiftsKey = `${dayConfig.key}_shifts`;

        if (!this.requestedScheduleChange[enabledKey]) return false;

        const shifts = this.requestedScheduleChange[shiftsKey];
        if (!shifts || shifts.length === 0) return false;

        // Check if all shifts have both start and end times
        return shifts.every((shift) => shift.start && shift.end);
      });

      return hasEnabledDayWithValidShifts;
    },
  },
  watch: {
    showTrainingHistory(val) {
      if (val) {
        this.loadTrainingHistory();
      }
    },
    showGrantClientAccessModal(val) {
      if (val) {
        this.loadingAvailableClients = true;
        this.selectedClientsToGrant = [];
        userClientAccessService.availableClients(this.caregiverId)
          .then((clients) => { this.availableClientsForAccess = clients; })
          .catch((err) => { this.$notifyError("Error loading clients: " + err); })
          .finally(() => { this.loadingAvailableClients = false; });
      }
    },
    scheduleViewMode(newMode) {
      if (newMode === "requested") {
        // Initialize the requested schedule when switching to requested mode
        this.initializeRequestedSchedule();
      }
    },
    activeTab(newTab) {
      // Auto-select appropriate view when Work Schedule tab is clicked
      if (newTab === "work-schedule") {
        // For caregivers viewing own page without a current schedule, pick the right mode
        if (this.isViewingOwnPage && !this.hasCurrentSchedule) {
          if (this.pendingScheduleChangeRequests.length > 0) {
            this.scheduleViewMode = 'pending';
          } else if (this.caregiverSchedules.some(s => s.is_upcoming)) {
            this.scheduleViewMode = 'current';
          } else {
            this.scheduleViewMode = 'requested';
          }
        } else {
          this.scheduleViewMode = "current";
        }

        // Don't reset admin view mode if navigated here with ?view=requested
        if (this.$route.query.view !== 'requested') {
          this.adminScheduleViewMode = "current";
        }

        // Find and select the current schedule in the dropdown
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const currentScheduleIndex = this.caregiverSchedules.findIndex(
          (schedule) => {
            const startDate = this.parseDateLocal(schedule.start_date);
            startDate.setHours(0, 0, 0, 0);
            const endDate = schedule.end_date
              ? this.parseDateLocal(schedule.end_date)
              : null;
            if (endDate) endDate.setHours(0, 0, 0, 0);
            return startDate <= today && (!endDate || endDate >= today);
          },
        );

        if (this.isApprovedScheduleCurrent) {
          // Select the current approved schedule change
          const upcomingCurrentIndex = this.caregiverSchedules.findIndex(s => {
            if (!s.is_upcoming) return false;
            const effective = this.parseDateLocal(s.start_date);
            effective.setHours(0, 0, 0, 0);
            return effective <= today;
          });
          if (upcomingCurrentIndex >= 0) {
            this.selectSchedule(upcomingCurrentIndex);
          }
        } else if (currentScheduleIndex >= 0) {
          this.selectSchedule(currentScheduleIndex);
        }
      }
    },
  },
  async mounted() {
    if (this.$route.query.tab) {
      this.activeTab = this.$route.query.tab;
    }
    if (this.$route.query.view === 'requested') {
      this.adminScheduleViewMode = 'requested';
    }

    this.updateDaysInMonth();
    await this.loadCaregiverData();

    // Load tab-specific data if navigated directly via URL
    if (this.activeTab === 'compliance') {
      this.loadComplianceProfile();
    } else if (this.activeTab === 'client-access') {
      this.loadClientAccess();
    } else if (this.activeTab === 'training') {
      this.loadTrainingTabData();
    }

    // Set correct schedule view mode after data loads
    if (this.isViewingOwnPage && !this.hasCurrentSchedule) {
      if (this.pendingScheduleChangeRequests.length > 0) {
        this.scheduleViewMode = 'pending';
      } else if (this.caregiverSchedules.some(s => s.is_upcoming)) {
        this.scheduleViewMode = 'current';
      } else {
        this.scheduleViewMode = 'requested';
      }
    }

    // Auto-select the current approved schedule change if one is effective today
    if (this.isApprovedScheduleCurrent) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const upcomingCurrentIndex = this.caregiverSchedules.findIndex(s => {
        if (!s.is_upcoming) return false;
        const effective = this.parseDateLocal(s.start_date);
        effective.setHours(0, 0, 0, 0);
        return effective <= today;
      });
      if (upcomingCurrentIndex >= 0) {
        this.selectSchedule(upcomingCurrentIndex);
      }
    }

    // Load options for Details tab
    await this.getLeads();
    await this.getAdmins();
  },
  methods: {
    formatTimezoneLabel,
    shortTzName(iana) {
      if (!iana) return "";
      const label = formatTimezoneLabel(iana);
      return label.split(" (")[0];
    },
    goBack() {
      this.$router.push({ name: "Caregivers" });
    },
    async loadCaregiverData() {
      this.loadingShifts = true;
      try {
        // Always load caregiver details (needed for name display on all tabs)
        // and calendar shifts in parallel
        await Promise.all([
          this.loadCaregiverDetails(),
          this.loadCaregiverShifts(),
        ]);

        // Load schedule data in parallel
        await Promise.all([
          this.loadCaregiverSchedule(),
          this.loadPendingScheduleChangeRequests(),
          this.loadApprovedScheduleChange(),
        ]);

        // Lazy-load training data only if training tab is active
        if (this.activeTab === 'training') {
          await Promise.all([
            this.loadTrainingRecords(),
            this.loadTrainingDocuments(),
            this.loadTrainingCertificateRecords(),
          ]);
        }
      } catch (error) {
        this.$notifyError("Error loading caregiver data: " + error);
      } finally {
        this.loadingShifts = false;
      }
    },
    async getLeads() {
      try {
        this.leadOptions = await caregiversService.getLeads();
      } catch (error) {
        // Error loading leads
      }
    },
    async getAdmins() {
      try {
        this.adminOptions = await caregiversService.getAdmins();
      } catch (error) {
        // Error loading admins
      }
    },
    async loadCaregiverDetails() {
      this.loadingCaregiver = true;
      try {
        const response = await caregiversService.getById(this.caregiverId);
        this.caregiver = response;

        // Update caregiver name from loaded data
        if (this.caregiver.first_name && this.caregiver.last_name) {
          this.caregiverName = `${this.caregiver.first_name} ${this.caregiver.last_name}`;
        }

        // Ensure address object exists
        if (!this.caregiver.address) {
          this.caregiver.address = {
            address1: "",
            address2: "",
            city: "",
            state: "",
            zip: "",
          };
        }

        // Infer timezone from caregiver address
        if (this.caregiver.address && this.caregiver.address.id) {
          try {
            const tzResult = await timezoneService.getFromAddress(this.caregiver.address.id);
            this.inferredCaregiverTimezone = tzResult?.timezone || null;
            this.scheduleTimezone = tzResult?.timezone || null;
          } catch (e) {
            // Leave timezone as null — admin will pick manually
          }
        }
      } catch (error) {
        this.$notifyError("Error loading caregiver details: " + error);
      } finally {
        this.loadingCaregiver = false;
      }
    },
    async saveCaregiverDetails() {
      try {
        this.$spinner.show();
        await caregiversService.update(this.caregiverId, this.caregiver);
        this.$notifySuccess("Caregiver details updated successfully");

        // Update caregiver name in case first or last name changed
        this.caregiverName = `${this.caregiver.first_name} ${this.caregiver.last_name}`;
      } catch (error) {
        this.$notifyError(
          error.response?.data?.message ||
            error.response?.data?.error ||
            "Error saving caregiver details"
        );
      } finally {
        this.$spinner.hide();
      }
    },
    applyCaregiverPlace(place) {
      if (!this.caregiver.address) return;
      this.$set(this.caregiver.address, "address1", place.address1);
      this.$set(this.caregiver.address, "city", place.city);
      this.$set(this.caregiver.address, "state", place.state);
      this.$set(this.caregiver.address, "zip", place.zip);
      this.$set(this.caregiver.address, "geocode", place.geocode);
    },
    openCaregiverMapPinPicker() {
      const addr = this.caregiver.address || {};
      this.mapPinPickerGeocode = addr.geocode || "";
      this.mapPinPickerAddress = { city: addr.city, state: addr.state, zip: addr.zip };
      this.showMapPinPicker = true;
    },
    async handleCaregiverMapPinSelected(location) {
      if (!this.caregiver.address) return;
      this.$set(this.caregiver.address, "geocode", location.geocode);
      try {
        this.$spinner.show();
        await caregiversService.update(this.caregiverId, this.caregiver);
        this.$notifySuccess("Location set successfully.");
      } catch (error) {
        this.$notifyError("Error saving location: " + (error.message || error));
      } finally {
        this.$spinner.hide();
      }
    },

    // Compliance tab methods
    async loadComplianceProfile() {
      if (!this.caregiverId) return;
      this.loadingCompliance = true;
      try {
        this.complianceProfile = await caregiverComplianceService.getComplianceProfile(this.caregiverId);
        // Load pending docs for admin actions
        this.pendingCompDocs = (this.complianceProfile.allDocuments || [])
          .filter(d => d.status === 'Pending')
          .map(d => ({ ...d, _approveDate: d.expiration_date || '' }));
      } catch (e) {
      } finally {
        this.loadingCompliance = false;
      }
    },

    async uploadComplianceDoc() {
      if (!this.compUploadType) {
        this.$notifyError('Please select a document type');
        return;
      }
      const fileInput = this.$refs.compFileInput;
      if (!fileInput || !fileInput.files || !fileInput.files[0]) {
        this.$notifyError('Please select a file');
        return;
      }
      this.uploadingCompDoc = true;
      try {
        const formData = new FormData();
        formData.append('file', fileInput.files[0]);
        formData.append('document_type', this.compUploadType);
        formData.append('tier', this.getDocTier(this.compUploadType));
        if (this.compUploadExpiration) {
          formData.append('expiration_date', this.compUploadExpiration);
        }
        await caregiverComplianceService.upload(this.caregiverId, formData);
        this.$notifySuccess('Document uploaded. Set the expiration date and approve it below.');
        fileInput.value = '';
        this.compUploadType = '';
        this.compUploadExpiration = '';
        this.compFileName = '';
        await this.loadComplianceProfile();
      } catch (e) {
        this.$notifyError('Upload failed: ' + (e.response?.data?.error || e.message));
      } finally {
        this.uploadingCompDoc = false;
      }
    },

    async selfUploadComplianceDoc(documentType, event) {
      const file = event.target.files[0];
      if (!file) return;
      this.selfUploadingDocType = documentType;
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('document_type', documentType);
        formData.append('tier', this.getDocTier(documentType));
        await caregiverComplianceService.upload(this.caregiverId, formData);
        this.$notifySuccess(`${documentType} uploaded successfully. It will be reviewed by a manager.`);
        await this.loadComplianceProfile();
      } catch (e) {
        this.$notifyError('Upload failed: ' + (e.response?.data?.error || e.message));
      } finally {
        this.selfUploadingDocType = null;
        event.target.value = '';
      }
    },

    getDocTier(docType) {
      const tierMap = {
        'Drivers License': 1, 'Auto Insurance': 1, 'Auto Registration': 1,
        'CPR': 2, 'SOAR': 3, 'ABI/TBI Certification': 4,
      };
      return tierMap[docType] || 1;
    },

    async approveCompDoc(doc) {
      if (!doc._approveDate) {
        this.$notifyError('Please set the expiration date before approving');
        return;
      }
      try {
        await caregiverComplianceService.approve(doc.id, doc._approveDate);
        this.$notifySuccess('Document approved');
        await this.loadComplianceProfile();
      } catch (e) {
        this.$notifyError('Approval failed: ' + (e.response?.data?.error || e.message));
      }
    },

    async rejectCompDoc(doc) {
      try {
        await caregiverComplianceService.reject(doc.id, 'Rejected by admin');
        this.$notifySuccess('Document rejected');
        await this.loadComplianceProfile();
      } catch (e) {
        this.$notifyError('Rejection failed: ' + (e.response?.data?.error || e.message));
      }
    },

    async grantCompWaiver(doc) {
      const waiverDate = doc._approveDate;
      if (!waiverDate) {
        this.$notifyError('Please set a waiver expiration date');
        return;
      }
      try {
        const formData = new FormData();
        formData.append('waiver_expiration_date', waiverDate);
        await caregiverComplianceService.grantWaiver(doc.id, formData);
        this.$notifySuccess('Waiver granted');
        await this.loadComplianceProfile();
      } catch (e) {
        this.$notifyError('Waiver failed: ' + (e.response?.data?.error || e.message));
      }
    },

    async viewCompDoc(docId) {
      try {
        const blob = await caregiverComplianceService.viewDocument(docId);
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
      } catch (e) {
        this.$notifyError('Could not open document: ' + (e.response?.data?.error || e.message));
      }
    },

    async downloadCompDoc(docId, documentType) {
      try {
        const blob = await caregiverComplianceService.downloadDocument(docId);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = (documentType || 'document').replace(/\s+/g, '_') + '_' + docId;
        link.click();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        this.$notifyError('Download failed: ' + (e.response?.data?.error || e.message));
      }
    },

    async viewCompWaiver(docId) {
      try {
        const blob = await caregiverComplianceService.viewWaiver(docId);
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
      } catch (e) {
        this.$notifyError('Could not open waiver: ' + (e.response?.data?.error || e.message));
      }
    },

    editCompDoc(item) {
      this.editingCompDoc = {
        id: item.detail.id,
        documentType: item.documentType,
        expirationDate: item.detail.expirationDate || '',
        status: item.detail.status || 'Pending',
      };
      this.showEditCompDocModal = true;
    },

    async saveEditCompDoc() {
      this.savingCompDoc = true;
      try {
        await caregiverComplianceService.update(this.editingCompDoc.id, {
          expiration_date: this.editingCompDoc.expirationDate || null,
          status: this.editingCompDoc.status,
        });
        this.$notifySuccess('Document updated');
        this.showEditCompDocModal = false;
        await this.loadComplianceProfile();
      } catch (e) {
        this.$notifyError('Update failed: ' + (e.response?.data?.error || e.message));
      } finally {
        this.savingCompDoc = false;
      }
    },

    async deleteCompDoc(docId, documentType) {
      try {
        await this.$confirm(`Delete ${documentType} document? This cannot be undone.`, 'Confirm Delete', {
          confirmButtonText: 'Delete',
          cancelButtonText: 'Cancel',
          type: 'warning',
        });
        await caregiverComplianceService.destroy(docId);
        this.$notifySuccess('Document deleted');
        await this.loadComplianceProfile();
      } catch (e) {
        if (e !== 'cancel') {
          this.$notifyError('Delete failed: ' + (e.response?.data?.error || e.message));
        }
      }
    },

    async loadTrainingHistory() {
      this.loadingTrainingHistory = true;
      try {
        this.trainingHistoryRecords = await caregiverComplianceService.getTrainingHistory(this.caregiverId);
      } catch (e) {
        this.trainingHistoryRecords = [];
      } finally {
        this.loadingTrainingHistory = false;
      }
    },

    complianceStatusColor() {
      if (!this.complianceProfile) return '#999';
      const s = this.complianceProfile.overallStatus;
      return s === 'green' ? '#67c23a' : s === 'yellow' ? '#e6a23c' : '#f44336';
    },

    complianceStatusIcon() {
      if (!this.complianceProfile) return 'fa-solid fa-circle';
      const s = this.complianceProfile.overallStatus;
      return s === 'green' ? 'fa-solid fa-check-circle' : s === 'yellow' ? 'fa-solid fa-exclamation-circle' : 'fa-solid fa-times-circle';
    },

    docStatusColor(status) {
      const map = { valid: '#67c23a', expiring_soon: '#e6a23c', expired: '#f44336', missing: '#909399', pending: '#409eff', waivered: '#FF9800', rejected: '#f44336' };
      return map[status] || '#999';
    },

    docStatusIcon(status) {
      const map = { valid: 'fa-solid fa-check-circle', expiring_soon: 'fa-solid fa-clock', expired: 'fa-solid fa-times-circle', missing: 'fa-solid fa-minus-circle', pending: 'fa-solid fa-hourglass-half', waivered: 'fa-solid fa-shield-halved', rejected: 'fa-solid fa-ban' };
      return map[status] || 'fa-solid fa-circle';
    },

    docStatusBadgeBg(status) {
      const map = { valid: '#67c23a', expiring_soon: '#e6a23c', expired: '#f44336', missing: '#909399', pending: '#409eff', waivered: '#FF9800', rejected: '#f44336' };
      return map[status] || '#999';
    },

    async loadCaregiverShifts() {
      const year = this.currentMonth.getFullYear();
      const month = this.currentMonth.getMonth();

      // Use the full displayed grid range (including padding days from adjacent months)
      // so that weekly totals are correct even when weeks span two months
      const startDate = this.daysInMonth.length > 0
        ? this.daysInMonth[0]
        : new Date(year, month, 1);
      const endDate = this.daysInMonth.length > 0
        ? this.daysInMonth[this.daysInMonth.length - 1]
        : new Date(year, month + 1, 0, 23, 59, 59, 999);

      const params = {
        startDate: startDate.toISOString().split("T")[0] + "T00:00:00.000Z",
        endDate: endDate.toISOString().split("T")[0] + "T23:59:59.999Z",
        userType: "Caregiver",
        userId: this.caregiverId,
        perPage: 1000,
      };

      const response = await shiftsService.getByRole(params);

      this.caregiverShifts = response.shifts.map((shift) => {
        let status = "scheduled";

        // For group shifts, check if the current user is an assigned caregiver
        // Use shift_type === 'Group' (not isDSPDGroupShift) so this works even without service_codes
        const store = useLoginStore();
        const currentUserId = store.user?.id;
        if (shift.shift_type === 'Group' && shift.caregivers && shift.caregivers.length > 0) {
          const isAssigned = shift.caregivers.some(c => Number(c.id) === Number(currentUserId));
          if (isAssigned) {
            const clockTimes = shift.caregiverClockTimes || [];
            const myClockTime = clockTimes.find(ct => Number(ct.id_users) === Number(currentUserId));
            const overallStatus = (shift.shift_status || shift.status || '').toLowerCase();
            const shiftDate = (shift.date || shift.scheduled_date || '').split('T')[0];
            const today = new Date();
            const todayStr = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
            const dayPast = shiftDate && shiftDate < todayStr;

            if (!myClockTime || !myClockTime.clock_in) {
              if (overallStatus === 'approved') status = 'approved';
              else if (overallStatus === 'missed') status = 'missed';
              else if (dayPast) status = 'missed';
              else status = 'scheduled';
            } else if (myClockTime.clock_in && !myClockTime.clock_out) {
              status = dayPast ? 'error' : 'clocked-in';
            } else if (myClockTime.clock_out) {
              status = overallStatus === 'approved' ? 'approved' : 'clocked-out';
            }
          } else if (isDSPDGroupShift(shift)) {
            const statuses = shift.caregivers.map(c => getCaregiverShiftStatus(shift, c.id));
            if (statuses.some(s => s === 'error')) status = 'error';
            else if (statuses.every(s => s === 'approved')) status = 'approved';
            else if (statuses.every(s => s === 'missed')) status = 'missed';
            else if (statuses.every(s => s === 'missed' || s === 'error')) status = 'error';
            else if (statuses.some(s => s === 'clocked-in')) status = 'clocked-in';
            else if (statuses.some(s => s === 'clocked-out')) status = 'clocked-out';
          }
        } else if (shift.shift_status === "Clocked Out") {
          status = "clocked-out";
        } else if (shift.shift_status === "Clocked In") {
          status = "clocked-in";
        } else if (shift.shift_status === "Approved") {
          status = "approved";
        } else if (shift.shift_status === "Missed") {
          status = "missed";
        } else if (shift.shift_status === "Scheduled") {
          status = "scheduled";
        } else if (shift.shift_status === "Canceled") {
          status = "canceled";
        }

        // No status set — check if the shift date has passed
        if (status === "scheduled" && !shift.shift_status) {
          const shiftDate = (shift.date || shift.scheduled_date || '').split('T')[0];
          if (shiftDate) {
            const today = new Date();
            const todayStr = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
            if (shiftDate < todayStr) {
              if (!shift.clockIn && !shift.clock_in) {
                status = 'missed';
              } else {
                status = 'error';
              }
            }
          }
        }

        const clientName =
          shift.payer === "Non Service"
            ? "N/A"
            : shift.clients && shift.clients.length > 0
              ? shift.clients[0].name
              : "Unknown Client";

        return {
          id: shift.id,
          date: shift.date,
          startTime: shift.scheduled_start || shift.startTime,
          endTime: shift.scheduled_end || shift.endTime,
          status: status,
          clientName: clientName,
          caregivers: shift.caregivers || [],
          clients: shift.clients || [],
          payer: shift.payer || "DSPD",
          shiftType: shift.shift_type || "1-on-1",
          groupName: shift.group_name || null,
          originalData: shift, // Store original shift data for modal access
        };
      });
    },
    async loadCaregiverSchedule() {
      try {
        // Load all schedules (no date range filter) so future/upcoming schedules
        // are always available for the schedule tabs
        const response = await schedulesService.getByCaregiver(
          this.caregiverId
        );

        // Handle new response structure with schedules and approvedTimeOff
        // Keep upcoming schedules in the array (needed for calendar display)
        // but upcoming ones come first so find() picks them for dates >= effective_date
        this.caregiverSchedules = response.schedules || [];
        this.caregiverApprovedTimeOff = response.approvedTimeOff || [];

        // Select the current schedule (the one active today), falling back to first real schedule
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        let bestIndex = this.caregiverSchedules.findIndex(s => {
          if (s.is_upcoming) return false;
          const startDate = this.parseDateLocal(s.start_date);
          startDate.setHours(0, 0, 0, 0);
          const endDate = s.end_date ? this.parseDateLocal(s.end_date) : null;
          if (endDate) endDate.setHours(0, 0, 0, 0);
          return startDate <= today && (!endDate || endDate >= today);
        });
        if (bestIndex < 0) {
          bestIndex = this.caregiverSchedules.findIndex(s => !s.is_upcoming);
        }
        if (bestIndex >= 0) {
          this.selectedScheduleIndex = bestIndex;
          this.caregiverSchedule = this.convertScheduleShiftsToLocal({
            ...this.caregiverSchedules[bestIndex],
          });
          this.ensureScheduleStructure();
          // Load timezone from saved schedule, but only mark as "inferred"
          // if we actually inferred it from an address (not just loaded from DB)
          if (this.caregiverSchedule.timezone) {
            this.scheduleTimezone = this.caregiverSchedule.timezone;
            // Only keep inferredCaregiverTimezone if it was set from the address lookup
            if (!this.inferredCaregiverTimezone) {
              this.inferredCaregiverTimezone = null;
            }
          }
        } else {
          this.createNewSchedule();
        }
      } catch (error) {
        this.createNewSchedule();
      }
    },
    async loadPendingScheduleChangeRequests() {
      if (!this.isAdmin && !this.isViewingOwnPage) return;

      this.loadingRequests = true;
      try {
        const response = await scheduleChangeRequestsService.list({
          user_id: this.caregiverId,
          status: "pending",
        });

        // Handle both paginated and non-paginated responses
        let requests;
        if (response && response.data) {
          requests = response.data;
        } else {
          requests = response || [];
        }

        // Parse requested_schedule if it comes back as a JSON string
        this.pendingScheduleChangeRequests = requests.map(req => {
          if (typeof req.requested_schedule === 'string') {
            req.requested_schedule = JSON.parse(req.requested_schedule);
          }
          return req;
        });
      } catch (error) {
        this.pendingScheduleChangeRequests = [];
      } finally {
        this.loadingRequests = false;
      }
    },
    async loadApprovedScheduleChange() {
      try {
        // Approved schedule changes are now displayed directly from caregiverSchedules
        // (as is_upcoming items from getByCaregiver). This method just loads for
        // backward compatibility with any code still referencing approvedScheduleChangeRequest.
        const response = await scheduleChangeRequestsService.list({
          status: 'approved',
          applied: false,
          user_id: this.caregiverId,
        });
        let requests;
        if (response && response.data) {
          requests = response.data;
        } else {
          requests = response || [];
        }
        if (requests.length > 0) {
          const req = requests[0];
          if (typeof req.requested_schedule === 'string') {
            req.requested_schedule = JSON.parse(req.requested_schedule);
          }
          this.approvedScheduleChangeRequest = req;
        } else {
          this.approvedScheduleChangeRequest = null;
        }
      } catch (error) {
        this.approvedScheduleChangeRequest = null;
      }
    },
    async loadApprovedTimeOff() {
      // Time off is now loaded as part of loadCaregiverSchedule
      // This method is kept for compatibility but does nothing
      // The approved time off data comes from schedulesService.getByCaregiver()
    },
    convertScheduleShiftsToLocal(schedule) {
      if (!schedule) return schedule;

      const convertedSchedule = { ...schedule };
      const days = [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday",
      ];

      days.forEach((day) => {
        const shiftsKey = `${day}_shifts`;
        const shifts = convertedSchedule[shiftsKey];

        if (shifts && Array.isArray(shifts)) {
          convertedSchedule[shiftsKey] = shifts.map((shift) => {
            return {
              start: this.extractTimeFromDateTime(shift.start),
              end: this.extractTimeFromDateTime(shift.end),
            };
          });

          // Set the enabled flag if there are shifts
          convertedSchedule[`${day}_enabled`] = shifts.length > 0;
        }
      });

      return convertedSchedule;
    },
    extractTimeFromDateTime(dateTimeString) {
      if (!dateTimeString) return "";

      // If it's already in HH:MM format, return as is
      if (dateTimeString.match(/^\d{2}:\d{2}$/)) {
        return dateTimeString;
      }

      // If it's a full datetime string, extract time
      try {
        const date = new Date(dateTimeString);
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        return `${hours}:${minutes}`;
      } catch (error) {
        return "";
      }
    },
    ensureScheduleStructure() {
      const days = [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday",
      ];

      days.forEach((day) => {
        const shiftsKey = `${day}_shifts`;
        const enabledKey = `${day}_enabled`;

        if (!this.caregiverSchedule[shiftsKey]) {
          this.caregiverSchedule[shiftsKey] = [];
        }

        if (this.caregiverSchedule[shiftsKey].length === 0) {
          this.caregiverSchedule[shiftsKey].push({ start: "", end: "" });
          this.caregiverSchedule[enabledKey] = false;
        } else {
          this.caregiverSchedule[enabledKey] = true;
        }
      });
    },
    createNewSchedule() {
      let newSchedule;

      // Find the first real schedule to copy from
      const currentSchedule = this.caregiverSchedules.find(s => s.id && !s.is_upcoming);

      if (currentSchedule) {
        newSchedule = this.convertScheduleShiftsToLocal({ ...currentSchedule });
        delete newSchedule.id;
        delete newSchedule.createdAt;
        delete newSchedule.updatedAt;
        newSchedule.start_date = this.todayDate;
        newSchedule.end_date = null;
        newSchedule.notes = "";
      } else if (this.caregiverSchedules.some(s => s.is_upcoming)) {
        // Copy from the most recent approved schedule change if no DB schedule exists yet
        const upcomingSchedule = this.caregiverSchedules.find(s => s.is_upcoming);
        newSchedule = this.convertScheduleShiftsToLocal({ ...upcomingSchedule });
        delete newSchedule.id;
        delete newSchedule.is_upcoming;
        delete newSchedule.schedule_change_request_id;
        delete newSchedule.createdAt;
        delete newSchedule.updatedAt;
        newSchedule.start_date = this.todayDate;
        newSchedule.end_date = null;
        newSchedule.notes = "";
      } else {
        newSchedule = {
          caregiver_id: this.caregiverId,
          start_date: this.todayDate,
          end_date: null,
          notes: "",
        };

        const days = [
          "monday",
          "tuesday",
          "wednesday",
          "thursday",
          "friday",
          "saturday",
          "sunday",
        ];

        days.forEach((day) => {
          newSchedule[`${day}_shifts`] = [];
          newSchedule[`${day}_enabled`] = false;
        });
      }

      this.caregiverSchedule = newSchedule;
      this.selectedScheduleIndex = -1;
    },
    selectSchedule(index) {
      this.selectedScheduleIndex = index;
      this.caregiverSchedule = this.convertScheduleShiftsToLocal({
        ...this.caregiverSchedules[index],
      });
      this.ensureScheduleStructure();
    },
    selectUpcomingSchedule() {
      const req = this.approvedScheduleChangeRequest;
      if (!req) return;

      const schedule = {
        caregiver_id: this.caregiverId,
        start_date: req.effective_date,
        end_date: null,
        notes: req.reason || "",
      };

      const requestedSchedule = req.requested_schedule;
      const days = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

      days.forEach((day) => {
        const shifts = requestedSchedule[`${day}_shifts`];
        if (shifts && shifts.length > 0) {
          schedule[`${day}_shifts`] = shifts.map(s => ({ ...s }));
          schedule[`${day}_enabled`] = true;
        } else {
          schedule[`${day}_shifts`] = [];
          schedule[`${day}_enabled`] = false;
        }
      });

      this.caregiverSchedule = schedule;
      this.selectedScheduleIndex = -2;
      this.ensureScheduleStructure();
    },
    async deleteCurrentSchedule() {
      if (!this.caregiverSchedule.id) return;

      const confirmed = await this.$confirmAction(
        () => {},
        "Are you sure you want to delete this schedule?",
      );

      if (confirmed) {
        try {
          await schedulesService.delete(this.caregiverSchedule.id);
          this.$notifySuccess("Schedule deleted successfully");
          await this.loadCaregiverSchedule();
        } catch (error) {
          this.$notifyError("Error deleting schedule: " + error);
        }
      }
    },
    getShiftsForDay(day) {
      const shiftsKey = `${day}_shifts`;
      if (!this.caregiverSchedule[shiftsKey]) {
        this.caregiverSchedule[shiftsKey] = [];
      }
      if (this.caregiverSchedule[shiftsKey].length === 0) {
        this.caregiverSchedule[shiftsKey].push({ start: "", end: "" });
      }
      return this.caregiverSchedule[shiftsKey];
    },
    onDayEnabledChange(day, enabled) {
      const enabledKey = `${day}_enabled`;
      const shiftsKey = `${day}_shifts`;

      this.caregiverSchedule[enabledKey] = enabled;

      if (!enabled) {
        this.caregiverSchedule[shiftsKey] = [];
      } else if (this.caregiverSchedule[shiftsKey].length === 0) {
        this.caregiverSchedule[shiftsKey] = [{ start: "09:00", end: "17:00" }];
      }
    },
    updateShiftTime(day, shiftIndex, timeType, newTime) {
      const shiftsKey = `${day}_shifts`;
      this.caregiverSchedule[shiftsKey][shiftIndex][timeType] = newTime;
    },
    addShift(day) {
      const shiftsKey = `${day}_shifts`;
      this.caregiverSchedule[shiftsKey].push({ start: "09:00", end: "17:00" });
    },
    removeShift(day, shiftIndex) {
      const shiftsKey = `${day}_shifts`;
      this.caregiverSchedule[shiftsKey].splice(shiftIndex, 1);
    },
    async saveSchedule() {
      if (!this.scheduleTimezone) {
        this.$notifyError("Timezone is required. Please select a timezone.");
        return;
      }
      try {
        this.$spinner.show();

        const scheduleData = { ...this.caregiverSchedule };
        scheduleData.timezone = this.scheduleTimezone;

        // Create clean payload
        const cleanPayload = this.createCleanSchedulePayload(scheduleData);

        if (this.caregiverSchedule.id) {
          await schedulesService.update(
            this.caregiverSchedule.id,
            cleanPayload,
          );
          this.$notifySuccess("Schedule updated successfully");
        } else {
          await schedulesService.create(cleanPayload);
          this.$notifySuccess("Schedule created successfully");
        }

        await this.loadCaregiverSchedule();
      } catch (error) {
        this.$notifyError("Error saving schedule: " + error);
      } finally {
        this.$spinner.hide();
      }
    },
    createCleanSchedulePayload(scheduleData) {
      const cleanPayload = {
        caregiver_id: this.caregiverId,
      };

      const basicFields = [
        "start_date",
        "end_date",
        "week_start_date",
        "notes",
        "timezone",
        "recurring",
      ];

      basicFields.forEach((field) => {
        if (scheduleData[field] !== undefined && scheduleData[field] !== null) {
          cleanPayload[field] = scheduleData[field];
        }
      });

      const days = [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday",
      ];

      days.forEach((day) => {
        const shiftsKey = `${day}_shifts`;
        const enabledKey = `${day}_enabled`;
        const shifts = scheduleData[shiftsKey];

        // Only include shifts for days that are enabled
        if (
          scheduleData[enabledKey] &&
          shifts &&
          Array.isArray(shifts) &&
          shifts.length > 0
        ) {
          const validShifts = shifts.filter(
            (shift) => shift.start && shift.end,
          );
          if (validShifts.length > 0) {
            cleanPayload[shiftsKey] = validShifts.map((shift) => ({
              start: this.convertTimeToUTC(shift.start, day),
              end: this.convertTimeToUTC(shift.end, day),
            }));
          }
        }
      });

      return cleanPayload;
    },
    convertTimeToUTC(timeString, dayOfWeek) {
      if (!timeString) return null;

      const referenceDate = this.getReferenceDate(dayOfWeek);
      const [hours, minutes] = timeString.split(":");

      const localDate = new Date(referenceDate);
      localDate.setHours(parseInt(hours, 10));
      localDate.setMinutes(parseInt(minutes, 10));
      localDate.setSeconds(0);
      localDate.setMilliseconds(0);

      return localDate.toISOString();
    },
    getReferenceDate(dayOfWeek) {
      const dayMap = {
        sunday: 0,
        monday: 1,
        tuesday: 2,
        wednesday: 3,
        thursday: 4,
        friday: 5,
        saturday: 6,
      };

      const today = new Date();
      const todayDay = today.getDay();
      const targetDay = dayMap[dayOfWeek];

      const daysUntilTarget = (targetDay - todayDay + 7) % 7;
      const referenceDate = new Date(today);
      referenceDate.setDate(today.getDate() + daysUntilTarget);
      referenceDate.setHours(0, 0, 0, 0);

      return referenceDate;
    },
    cancelScheduleEdit() {
      if (this.selectedScheduleIndex >= 0) {
        this.caregiverSchedule = this.convertScheduleShiftsToLocal({
          ...this.caregiverSchedules[this.selectedScheduleIndex],
        });
        this.ensureScheduleStructure();
      } else {
        this.activeTab = "calendar";
      }
    },
    formatScheduleLabel(schedule) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const startDate = this.parseDateLocal(schedule.start_date);
      startDate.setHours(0, 0, 0, 0);
      const endDate = schedule.end_date ? this.parseDateLocal(schedule.end_date) : null;
      if (endDate) {
        endDate.setHours(0, 0, 0, 0);
      }

      // Check if this is the current schedule
      const isCurrent = startDate <= today && (!endDate || endDate >= today);

      if (isCurrent) {
        return "Current";
      } else {
        // Future schedule: show as "Upcoming Schedule Feb 26, 2026"
        const formatted = startDate.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
        return "Upcoming Schedule " + formatted;
      }
    },
    updateDaysInMonth() {
      const year = this.currentMonth.getFullYear();
      const month = this.currentMonth.getMonth();
      const arr = [];
      const first = new Date(year, month, 1);
      const firstWeekday = first.getDay();

      for (let i = 0; i < firstWeekday; i++) {
        arr.push(new Date(year, month, i - firstWeekday + 1));
      }

      const d = new Date(year, month, 1);
      while (d.getMonth() === month) {
        arr.push(new Date(d));
        d.setDate(d.getDate() + 1);
      }

      const total = arr.length;
      const trailing = (7 - (total % 7)) % 7;
      for (let i = 1; i <= trailing; i++) {
        arr.push(new Date(year, month + 1, i));
      }

      this.daysInMonth = arr;
    },
    async goToPreviousMonth() {
      const nm = new Date(this.currentMonth);
      nm.setMonth(nm.getMonth() - 1);
      this.currentMonth = nm;
      this.updateDaysInMonth();
      await this.reloadCaregiverShifts();
    },
    async goToNextMonth() {
      const nm = new Date(this.currentMonth);
      nm.setMonth(nm.getMonth() + 1);
      this.currentMonth = nm;
      this.updateDaysInMonth();
      await this.reloadCaregiverShifts();
    },
    async reloadCaregiverShifts() {
      this.loadingShifts = true;
      try {
        await this.loadCaregiverShifts();
        // Approved time off is loaded with the schedule, so we reload that too
        await this.loadCaregiverSchedule();
      } catch (error) {
        this.$notifyError("Error loading shifts: " + error);
      } finally {
        this.loadingShifts = false;
      }
    },
    shiftsForDate(dateObj) {
      const y = dateObj.getFullYear();
      const m = String(dateObj.getMonth() + 1).padStart(2, "0");
      const d = String(dateObj.getDate()).padStart(2, "0");
      const dateStr = `${y}-${m}-${d}`;

      return this.caregiverShifts
        .filter((shift) => shift.date === dateStr)
        .map((shift) => {
          let statusClass = "status-scheduled";
          if (shift.status === "missed") {
            statusClass = "status-missed";
          } else if (shift.status === "approved") {
            statusClass = "status-approved";
          } else if (shift.status === "clocked-out") {
            statusClass = "status-clocked-out";
          } else if (shift.status === "clocked-in") {
            statusClass = "status-clocked-in";
          } else if (shift.status === "error") {
            statusClass = "status-error";
          } else if (shift.status === "canceled") {
            statusClass = "status-canceled";
          }

          // Determine display times based on clock in/out status
          const hasClockIn =
            shift.status === "clocked-in" ||
            shift.status === "clocked-out" ||
            shift.status === "approved";
          const hasClockOut =
            shift.status === "clocked-out" || shift.status === "approved";

          // Convert actual clock in/out times from UTC to local HH:MM
          const convertUTCDateTimeToLocal = (utcDateTimeStr) => {
            if (!utcDateTimeStr) return null;
            const utcDate = new Date(utcDateTimeStr);
            if (isNaN(utcDate.getTime())) return null;
            return `${String(utcDate.getHours()).padStart(2, "0")}:${String(utcDate.getMinutes()).padStart(2, "0")}`;
          };

          // For group shifts, clock times are per-caregiver in caregiverClockTimes
          const caregiverClockTime =
            shift.originalData?.caregiverClockTimes?.find(
              (ct) =>
                String(ct.id_users) === String(this.caregiverId),
            );

          const clockInTime =
            shift.originalData?.clock_in ||
            shift.originalData?.clockIn ||
            caregiverClockTime?.clock_in;
          const clockOutTime =
            shift.originalData?.clock_out ||
            shift.originalData?.clockOut ||
            caregiverClockTime?.clock_out;

          const actualStartTime = clockInTime
            ? convertUTCDateTimeToLocal(clockInTime)
            : null;
          const actualEndTime = clockOutTime
            ? convertUTCDateTimeToLocal(clockOutTime)
            : null;

          // Use actual times if clocked in/out, otherwise use scheduled times
          const displayStartTime =
            hasClockIn && actualStartTime ? actualStartTime : shift.startTime;
          const displayEndTime =
            hasClockOut && actualEndTime ? actualEndTime : shift.endTime;

          // On caregiver calendar, show client name as primary (caregiver is already known)
          let clientDisplay = shift.clientName || "Unassigned";

          // For group shifts, use groupName
          let groupName = null;
          if (shift.clients && shift.clients.length > 1) {
            groupName = shift.groupName || "Multiple";
          }

          return {
            ...shift,
            formattedStartTime: this.formatTime(displayStartTime),
            formattedEndTime: this.formatTime(displayEndTime),
            formattedScheduledStartTime: this.formatTime(
              shift.originalData?.scheduled_start || shift.startTime,
            ),
            formattedScheduledEndTime: this.formatTime(
              shift.originalData?.scheduled_end || shift.endTime,
            ),
            scheduledStartTime: shift.startTime,
            scheduledEndTime: shift.endTime,
            actualStartTime: actualStartTime,
            actualEndTime: actualEndTime,
            clockInRaw: clockInTime || null,
            clockOutRaw: clockOutTime || null,
            client: groupName ? null : clientDisplay,
            caregiver: null,
            groupName: groupName || null,
            statusClass: statusClass,
            payer: shift.payer || "DSPD",
            shiftType: shift.shiftType || "1-on-1",
            hasClockIn: hasClockIn,
            hasClockOut: hasClockOut,
            miles: milesForCaregiver(shift.originalData, this.caregiverId),
          };
        });
    },
    isToday(dateObj) {
      const now = new Date();
      return (
        dateObj.getFullYear() === now.getFullYear() &&
        dateObj.getMonth() === now.getMonth() &&
        dateObj.getDate() === now.getDate()
      );
    },
    formatTime(timeString) {
      if (!timeString) return "";

      // Handle ISO 8601 datetime strings (e.g., "2025-10-15T15:00:00.000Z")
      if (timeString.includes("T")) {
        const date = new Date(timeString);
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? "pm" : "am";
        let displayHours = hours % 12;
        displayHours = displayHours ? displayHours : 12;
        return `${displayHours}:${minutes.toString().padStart(2, "0")}${ampm}`;
      }

      // Handle time-only strings (e.g., "15:00")
      const timeParts = timeString.split(":");
      let hours = parseInt(timeParts[0]);
      const minutes = timeParts[1] || "00";
      const ampm = hours >= 12 ? "pm" : "am";
      hours = hours % 12;
      hours = hours ? hours : 12;
      return `${hours}:${minutes}${ampm}`;
    },
    getScheduledHoursForDate(dateObj) {
      const dayOfWeek = dateObj.getDay();
      const dayNames = [
        "sunday",
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
      ];
      const dayName = dayNames[dayOfWeek];

      // Convert date to YYYY-MM-DD format for comparison
      const dateStr = dateObj.toISOString().split("T")[0];

      // Find the most specific schedule for this date.
      // When multiple schedules overlap (e.g., none have end_dates),
      // pick the one with the latest start_date that is still <= date.
      // This ensures the most recently started schedule takes priority.
      const sortedSchedules = [...this.caregiverSchedules].sort((a, b) => {
        // Sort by start_date descending so the latest-starting schedule is checked first
        if (a.start_date > b.start_date) return -1;
        if (a.start_date < b.start_date) return 1;
        return 0;
      });

      const applicableSchedule = sortedSchedules.find((schedule) => {
        const startDate = schedule.start_date;
        const endDate = schedule.end_date;

        // Date must be >= start_date
        if (dateStr < startDate) return false;

        // If there's an end_date, date must be <= end_date
        if (endDate && dateStr > endDate) return false;

        return true;
      });

      if (!applicableSchedule || !applicableSchedule[`${dayName}_shifts`]) {
        return [];
      }

      const shifts = applicableSchedule[`${dayName}_shifts`];
      return shifts
        .filter((shift) => shift.start && shift.end)
        .map((shift) => ({
          start: this.extractTimeFromDateTime(shift.start),
          end: this.extractTimeFromDateTime(shift.end),
        }));
    },
    async createShift() {
      // Load all clients for the dropdown
      await this.loadAllClients();

      // Pre-populate the shift with the current caregiver
      this.shiftBeingEdited = {
        caregiverId: this.caregiverId ? Number(this.caregiverId) : null,
        payer: "VR",
      };
      this.showShiftModal = true;
    },
    viewShiftDetails(shift) {
      this.selectedShift = shift;
      this.selectedShiftForActions = shift;

      // Check if shift is approved - if so, show Shift Details modal instead of Edit
      const isShiftApproved = shift.status === "approved";

      // Check if shift is missed - if so, show action modal to allow approval
      const isShiftMissed = shift.status === "missed";

      // Check if user can clock in, clock out, or edit
      const canClockIn = this.canUserClockInToShift(shift);
      const canClockOut = this.canUserClockOutOfShift(shift);
      const canEdit = this.canUserEditShift();

      // If shift is approved, show the action modal (allows Undo Approval for Admins)
      if (isShiftApproved) {
        this.isViewOnlyMode = false;
        this.showShiftActionModal = true;
        return;
      }

      // If shift is missed, show action modal (admin/lead will see approve button)
      if (isShiftMissed) {
        this.isViewOnlyMode = false;
        this.showShiftActionModal = true;
        return;
      }

      // If user can't clock in but can edit, go straight to edit
      // EXCEPT for clocked-in, clocked-out, or error shifts where admins should see action modal for time editing
      // EXCEPT for assigned admins who need to see the action modal for clock-in/out buttons
      const isShiftClockedIn =
        shift.status === "clocked-in" || shift.status === "clocked in";
      const isShiftClockedOut =
        shift.status === "clocked-out" || shift.status === "clocked out";
      const isShiftError = shift.status === "error";
      const isAssignedCaregiver = (shift.originalData?.caregivers || shift.caregivers || []).some(
        (c) => Number(c.id) === Number(this.userId)
      );

      if (
        !canClockIn &&
        canEdit &&
        !isShiftClockedIn &&
        !isShiftClockedOut &&
        !isShiftError &&
        !isAssignedCaregiver
      ) {
        this.editShift(shift);
        return;
      }

      // If user can't clock in, can't clock out, and can't edit, show view-only modal
      // Exception: assigned caregivers should still get the action modal so ShiftActionModal
      // can evaluate its own canClockIntoShift logic
      if (!canClockIn && !canClockOut && !canEdit && !isAssignedCaregiver) {
        this.isViewOnlyMode = true;
        this.showShiftActionModal = true;
        return;
      }

      // Reset view-only mode for normal cases (user can perform some action)
      this.isViewOnlyMode = false;

      // Show the action modal which will determine available actions
      this.showShiftActionModal = true;
    },

    canUserClockInToShift(shift) {
      // Only admins and leads can clock in
      if (!this.isAdmin && !this.isLead) {
        return false;
      }

      // For group shifts, check the individual caregiver's status
      // (the overall shift status may not be "scheduled" if another caregiver already clocked in)
      const shiftData = shift.originalData || shift;
      let isScheduled;
      if (isDSPDGroupShift(shiftData)) {
        const caregiverStatus = getCaregiverShiftStatus(shiftData, this.caregiverId);
        isScheduled = caregiverStatus === 'scheduled';
      } else {
        isScheduled = shift.status === "scheduled";
      }

      return isScheduled;
    },

    canUserClockOutOfShift(shift) {
      // Only admins and leads can clock out
      if (!this.isAdmin && !this.isLead) {
        return false;
      }

      // For group shifts, check the individual caregiver's status
      // (the overall shift status may not be "clocked-in" if another caregiver already clocked out)
      const shiftData = shift.originalData || shift;
      let isClockedIn;
      if (isDSPDGroupShift(shiftData)) {
        const caregiverStatus = getCaregiverShiftStatus(shiftData, this.caregiverId);
        isClockedIn = caregiverStatus === 'clocked-in';
      } else {
        isClockedIn =
          shift.status === "clocked-in" || shift.status === "clocked in";
      }

      return isClockedIn;
    },

    canUserEditShift() {
      // Admins, Leads, and Employment Specialists can edit shifts
      return this.isAdmin || this.isLead || this.isEmploymentSpecialist;
    },

    async loadAllClients() {
      try {
        const response = await clientsService.list({ perPage: 1000, slim: true });
        const clients = response.data || response || [];

        // Map clients to ensure they have the correct structure (id and name fields)
        this.availableClients = clients.map(client => ({
          id: client.id,
          name: client.name || (client.first_name && client.last_name
            ? `${client.first_name} ${client.last_name}`.trim()
            : client.client_name) || 'Unknown Client'
        }));
      } catch (error) {
        this.availableClients = [];
      }
    },

    async editShift(shift) {
      // Load all clients for the dropdown
      await this.loadAllClients();

      // Extract clientId from clients array
      const clientIds =
        shift.clients && shift.clients.length > 0
          ? shift.clients.map((c) => c.id)
          : [];

      // Build the shift object - spread originalData first to get ALL fields including service codes
      const shiftToEdit = {
        ...shift.originalData,  // Spread ALL data from backend (includes service_codes, etc.)
        // Then override specific fields
        id: shift.id,
        date: shift.date,
        scheduled_start: shift.originalData?.scheduled_start || shift.startTime,
        scheduled_end: shift.originalData?.scheduled_end || shift.endTime,
        shift_type: shift.shiftType || shift.originalData?.shift_type,
        payer: shift.payer || shift.originalData?.payer,
        shift_status: shift.originalData?.shift_status || shift.status,
        clientId: clientIds.length > 0 ? clientIds[0] : null,
        caregiverId: this.caregiverId,
        clients: shift.originalData?.clients || [],
        caregivers: shift.originalData?.caregivers || [],
      };

      // Handle clocked-out/error shifts - route to appropriate edit modal
      const status = (shift.status || shift.originalData?.shift_status)?.toLowerCase();
      const isClockedOut = status === "clocked-out" || status === "clocked out" || status === "error";
      if (isClockedOut) {
        const mappedShift = {
          ...shiftToEdit,
          startTime: shiftToEdit.scheduled_start,
          endTime: shiftToEdit.scheduled_end,
          originalData: {
            ...shift.originalData,
            scheduled_start: shiftToEdit.scheduled_start,
            scheduled_end: shiftToEdit.scheduled_end,
          },
        };

        const loginStore = useLoginStore();
        const userRole = loginStore.user?.user_type || "Caregiver";
        const userId = loginStore.user?.id;

        // Check if the current user is the assigned caregiver
        const caregivers = shiftToEdit.caregivers || [];
        const isAssignedCaregiver = caregivers.some(
          (c) => Number(c.id) === Number(userId)
        );

        // Admins/Leads who are NOT assigned use EditClockOutTimeModal
        if (
          (userRole === "Admin" || userRole === "Lead") &&
          !isAssignedCaregiver
        ) {
          this.handleEditClockOutTime(shift);
          return;
        }

        // Everyone else (caregivers, ES, assigned admins) edits notes
        this.shiftToEditNotes = mappedShift;
        this.showEditShiftNotesModal = true;
        return;
      }

      this.shiftBeingEdited = shiftToEdit;
      this.showShiftModal = true;
    },

    handleEditShiftNotes(shift) {
      const originalData = shift.originalData || shift;
      const mappedShift = {
        ...originalData,
        id: shift.id,
        date: shift.date,
        startTime: originalData.scheduled_start,
        endTime: originalData.scheduled_end,
        originalData: {
          ...originalData,
          scheduled_start: originalData.scheduled_start,
          scheduled_end: originalData.scheduled_end,
        },
      };
      this.shiftToEditNotes = mappedShift;
      this.showEditShiftNotesModal = true;
    },

    closeShiftModal() {
      this.showShiftModal = false;
      this.shiftBeingEdited = null;
    },

    async saveShift({ shift, shifts, isEditing, isGroupShift, isDSPDGroupShift, exceptionShiftIds }) {
      try {
        this.$spinner.show();

        // Handle DSPD group shifts (single shift with multiple clients and task assignments)
        if (isDSPDGroupShift && shift && !isEditing) {
          await shiftsService.create(shift);

          const clientCount = shift.clientIds?.length || 1;
          this.$notifySuccess(`DSPD group shift created successfully for ${clientCount} client(s)!`);
          this.closeShiftModal();
          await this.loadCaregiverShifts();
          return;
        }

        // Handle group shifts (multiple shifts to create)
        if (isGroupShift && shifts && Array.isArray(shifts)) {
          const createdShifts = [];
          for (const shiftData of shifts) {
            const savedShift = await shiftsService.create(shiftData);
            createdShifts.push(savedShift);
          }

          this.$notifySuccess(`${createdShifts.length} group shifts created successfully!`);
          this.closeShiftModal();
          await this.loadCaregiverShifts();
          return;
        }

        // Normalize association IDs the same way the Shifts/Calendar views do.
        // Sending caregiverIds (an empty array when the caregiver field is
        // cleared) is what lets the backend actually unassign the caregiver;
        // omitting the key leaves the existing assignment untouched.
        const shiftData = {
          ...shift,
          clientIds:
            shift.clientIds || (shift.clientId ? [shift.clientId] : []),
          caregiverIds:
            shift.caregiverIds || (shift.caregiverId ? [shift.caregiverId] : []),
          serviceCodeIds:
            shift.serviceCodeIds || shift.selectedServiceCodes || [],
        };

        let savedShift;
        if (isEditing && shift.id) {
          savedShift = await shiftsService.update(shift.id, shiftData);
          this.$notifySuccess("Shift updated successfully");
        } else {
          savedShift = await shiftsService.create(shiftData);
          this.$notifySuccess("Shift created successfully");
        }

        // If the scheduler chose to keep individually-changed occurrences, move
        // them onto the new series the edit created (a series edit splits the
        // old series and starts a new one; exceptions stay tied to the old one
        // until re-parented).
        if (exceptionShiftIds && exceptionShiftIds.length > 0 && savedShift) {
          try {
            const newRecurringShiftId =
              savedShift.newRecurringShiftId || savedShift.id;
            if (newRecurringShiftId) {
              await shiftsService.handleExceptionShifts({
                action: "keep",
                exceptionShiftIds,
                newRecurringShiftId,
              });
              this.$notifySuccess("Changed occurrences kept on the new series");
            }
          } catch (exceptionError) {
            this.$notifyError(
              "Shift saved, but the changed occurrences could not be moved to the new series",
            );
          }
        }

        this.closeShiftModal();
        await this.loadCaregiverShifts();
      } catch (error) {
        // Client overlap is overridable: offer "Schedule anyway" and retry
        // with the acknowledgement flag (managers get notified server-side).
        // Caregiver availability is overridable too, with a confirm that
        // states the payroll/report consequences before the retry.
        if (await this.confirmCaregiverOverride(error)) {
          if (shift) shift.acknowledgeCaregiverConflict = true;
          (shifts || []).forEach((s) => {
            if (s) s.acknowledgeCaregiverConflict = true;
          });
          return this.saveShift({
            shift,
            shifts,
            isEditing,
            isGroupShift,
            isDSPDGroupShift,
          });
        }
        if (await this.confirmClientOverlap(error)) {
          if (shift) shift.acknowledgeClientOverlap = true;
          (shifts || []).forEach((s) => {
            if (s) s.acknowledgeClientOverlap = true;
          });
          return this.saveShift({
            shift,
            shifts,
            isEditing,
            isGroupShift,
            isDSPDGroupShift,
          });
        }
        // Rich, itemized overlap message when conflict detail is present.
        this.handleShiftSaveError(error);
      } finally {
        this.$spinner.hide();
      }
    },

    async handleClockIn(shift) {
      if (!(await this.$clockNudge("in"))) return;
      this.shiftToClockIn = shift.originalData || shift;
      this.showClockInModal = true;
    },

    async handleClockInSuccess() {
      await this.loadCaregiverShifts();
      this.$root.$emit("shift-saved");
    },

    handleOpenGroupTasks(shift) {
      this.groupTasksShift = shift.originalData || shift;
      this.showGroupTasksModal = true;
    },

    async handleClockOut(shift) {
      if (!(await this.$clockNudge("out"))) return;
      // Open the Clock Out Modal instead of making the API call directly
      // Spread originalData FIRST to get all backend fields including service_codes
      this.shiftToClockOut = {
        ...shift.originalData, // Spread ALL data from backend (includes service_codes, etc.)
        // Then override specific fields
        id: shift.id,
        date: shift.date,
        startTime: shift.startTime,
        endTime: shift.endTime,
        scheduled_start: shift.originalData?.scheduled_start || shift.startTime,
        scheduled_end: shift.originalData?.scheduled_end || shift.endTime,
        clients: shift.clients || shift.originalData?.clients || [],
        caregivers: shift.originalData?.caregivers || [],
        payer: shift.payer || shift.originalData?.payer,
        shift_type: shift.shiftType || shift.originalData?.shift_type,
      };
      this.showClockOutModal = true;
      this.showShiftActionModal = false;
    },

    async handleClockOutSuccess() {
      // Reload caregiver shifts after successful clock out
      await this.loadCaregiverShifts();
    },

    handleAdminClockOut(shift) {
      this.shiftToAdminClockOut = shift;
      this.showAdminClockOutModal = true;
      this.showShiftActionModal = false;
    },

    handleEditClockInTime(shift) {
      this.shiftToEditClockInTime = shift;
      this.showEditClockInTimeModal = true;
    },

    handleEditClockOutTime(shift) {
      this.shiftToEditClockOutTime = shift;
      this.showEditClockOutTimeModal = true;
    },

    handleApproveShift(shift) {
      // Map the shift data for the approve shift modal
      // Spread originalData FIRST to get all backend fields including shift_notes at top level
      this.shiftToApprove = {
        ...shift.originalData, // Spread ALL data from backend first
        // Then override specific fields
        startTime: shift.startTime || shift.originalData?.scheduled_start,
        endTime: shift.endTime || shift.originalData?.scheduled_end,
        scheduledStartTime:
          shift.originalData?.scheduled_start || shift.startTime,
        scheduledEndTime: shift.originalData?.scheduled_end || shift.endTime,
        clockInTime:
          shift.originalData?.clockIn || shift.originalData?.clock_in,
        clockOutTime:
          shift.originalData?.clockOut || shift.originalData?.clock_out,
        client: shift.client || shift.clientName,
        caregiver: shift.caregiver || this.caregiverName,
        caregiverId: this.caregiverId,
        clientId:
          shift.clients && shift.clients.length > 0
            ? shift.clients[0].id
            : null,
        payer: shift.payer || shift.originalData?.payer,
        shiftType: shift.shiftType || shift.originalData?.shift_type,
        originalData: {
          ...shift.originalData,
          scheduled_start: shift.originalData?.scheduled_start,
          scheduled_end: shift.originalData?.scheduled_end,
          clockIn: shift.originalData?.clockIn || shift.originalData?.clock_in,
          clock_in: shift.originalData?.clockIn || shift.originalData?.clock_in,
          clockOut:
            shift.originalData?.clockOut || shift.originalData?.clock_out,
          clock_out:
            shift.originalData?.clockOut || shift.originalData?.clock_out,
        },
      };
      this.showApproveShiftModal = true;
    },

    async handleUndoApproval(shift) {
      try {
        const confirmed = await this.$confirmAction(
          () => {},
          "Are you sure you want to undo this shift approval? This will restore the authorization hours.",
        );

        if (confirmed) {
          this.$spinner.show();
          await shiftsService.undoShiftApproval({ shiftId: shift.id });

          this.$notifySuccess(
            "Shift approval undone successfully! Authorization hours have been restored.",
          );

          // Close the modal and reload shifts
          this.showShiftActionModal = false;
          await this.loadCaregiverShifts();
        }
      } catch (error) {
        this.$notifyError("Error undoing approval: " + error);
      } finally {
        this.$spinner.hide();
      }
    },

    handleApproveMissedShift(shift) {
      // Map the shift data for the approve missed shift modal
      // Spread originalData FIRST to get all backend fields including service_codes
      const mappedShift = {
        ...shift.originalData,  // Spread ALL data from backend
        // Then override specific fields
        scheduledStartTime:
          shift.originalData?.scheduled_start || shift.startTime,
        scheduledEndTime: shift.originalData?.scheduled_end || shift.endTime,
        client: shift.client || shift.clientName,
        caregiver: shift.caregiver || this.caregiverName,
        caregiverId: this.caregiverId,
        clientId:
          shift.clients && shift.clients.length > 0
            ? shift.clients[0].id
            : null,
        payer: shift.payer || shift.originalData?.payer,
        shiftType: shift.shiftType || shift.originalData?.shift_type,
      };

      this.shiftToApproveMissed = mappedShift;
      this.showApproveMissedShiftModal = true;
    },

    async handleCancelShift(shift) {
      const shiftId = shift.id || shift.originalData?.id;
      if (!shiftId) return;
      let reason;
      try {
        const result = await this.$prompt(
          "Please provide a reason for canceling this shift:",
          "Cancel Shift",
          {
            confirmButtonText: "Cancel Shift",
            cancelButtonText: "Go Back",
            inputType: "textarea",
            inputPlaceholder: "e.g., client canceled, caregiver sick, weather...",
            inputValidator: (val) => {
              if (!val || !val.trim()) return "A cancellation reason is required";
              return true;
            },
          },
        );
        reason = result.value;
      } catch {
        return;
      }
      try {
        await shiftsService.update(shiftId, {
          shift_status: "Canceled",
          adminChangeReason: reason.trim(),
        });
        this.$notifySuccess("Shift canceled successfully");
        await this.loadCaregiverShifts();
      } catch (error) {
        this.$notifyError(error.response?.data?.message || "Error canceling shift");
      }
    },

    handleEditModalDelete(shift) {
      this.closeShiftModal();
      this.handleShiftDeletion(shift);
    },
    // Delete a shift, mirroring the Calendar page: recurring shifts get the
    // 3-option dialog, others a simple confirm.
    async handleShiftDeletion(shift) {
      const original = shift.originalData || shift;
      const isRecurring =
        original.recurring ||
        original.parent_recurring_shift_id ||
        shift.recurring ||
        shift.parent_recurring_shift_id;

      try {
        if (isRecurring) {
          this.deleteRecurringDialog = { visible: true, shift };
        } else {
          const confirmDelete = await this.$confirmAction(
            null,
            "Are you sure you want to delete this shift?",
          );
          if (!confirmDelete) return;
          await shiftsService.delete(shift.id);
          this.$notifySuccess("Shift deleted successfully!");
          this.showShiftActionModal = false;
          await this.loadCaregiverShifts();
        }
      } catch (error) {
        this.$notifyError("Error deleting shift: " + error);
      }
    },
    async deleteThisShiftOnly() {
      const shift = this.deleteRecurringDialog.shift;
      this.deleteRecurringDialog = { visible: false, shift: null };
      try {
        const shiftDate = shift.date ? shift.date.split("T")[0] : null;
        await shiftsService.deleteWithDate(shift.id, shiftDate);
        this.$notifySuccess("Shift occurrence deleted successfully!");
        this.showShiftActionModal = false;
        await this.loadCaregiverShifts();
      } catch (error) {
        this.$notifyError("Error deleting shift: " + error);
      }
    },
    async deleteThisAndFutureShifts() {
      const shift = this.deleteRecurringDialog.shift;
      this.deleteRecurringDialog = { visible: false, shift: null };
      try {
        const original = shift.originalData || shift;
        const parentId =
          original.parent_recurring_shift_id ||
          shift.parent_recurring_shift_id ||
          shift.id;
        const shiftDate = shift.date ? shift.date.split("T")[0] : null;
        await shiftsService.deleteFromDate(parentId, shiftDate);
        this.$notifySuccess("This and all future occurrences deleted successfully!");
        this.showShiftActionModal = false;
        await this.loadCaregiverShifts();
      } catch (error) {
        this.$notifyError("Error deleting shifts: " + error);
      }
    },
    async deleteAllShifts() {
      const shift = this.deleteRecurringDialog.shift;
      this.deleteRecurringDialog = { visible: false, shift: null };
      try {
        const original = shift.originalData || shift;
        const parentId =
          original.parent_recurring_shift_id ||
          shift.parent_recurring_shift_id ||
          shift.id;
        await shiftsService.deleteEntireSeries(parentId);
        this.$notifySuccess("Entire recurring series deleted successfully!");
        this.showShiftActionModal = false;
        await this.loadCaregiverShifts();
      } catch (error) {
        this.$notifyError("Error deleting series: " + error);
      }
    },

    handlePasswordChangeSuccess() {
      this.showPasswordChangeModal = false;
      this.$notifySuccess("Password changed successfully!");
    },

    calculateWeeklyTotal(schedule) {
      let totalMinutes = 0;
      this.dayConfigs.forEach(dayConfig => {
        const shifts = schedule[dayConfig.key + '_shifts'] || [];
        shifts.forEach(shift => {
          if (shift.start && shift.end) {
            const [sh, sm] = shift.start.split(':').map(Number);
            const [eh, em] = shift.end.split(':').map(Number);
            let diff = (eh * 60 + em) - (sh * 60 + sm);
            if (diff < 0) diff += 24 * 60;
            totalMinutes += diff;
          }
        });
      });
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      return `${hours}h ${minutes}m`;
    },
    getUpcomingShifts(day) {
      if (!this.approvedScheduleChangeRequest || !this.approvedScheduleChangeRequest.requested_schedule) return [];
      return this.approvedScheduleChangeRequest.requested_schedule[`${day}_shifts`] || [];
    },
    getPendingShifts(day) {
      if (!this.pendingScheduleChangeRequests.length) return [];
      const req = this.pendingScheduleChangeRequests[0];
      if (!req || !req.requested_schedule) return [];
      return req.requested_schedule[`${day}_shifts`] || [];
    },

    getRequestShifts(day) {
      const request = this.selectedPendingRequest;
      if (!request || !request.requested_schedule) return [];
      return request.requested_schedule[`${day}_shifts`] || [];
    },

    async approveScheduleRequest() {
      this.reviewSubmitting = true;
      const effectiveDate = this.selectedPendingRequest?.effective_date;
      try {
        await scheduleChangeRequestsService.reviewRequest(
          this.selectedPendingRequest.id,
          'approved'
        );
        this.$notifySuccess(`Schedule${this.hasCurrentSchedule ? ' change' : ''} request approved successfully`);
        this.selectedPendingRequestIndex = 0;
        this.adminScheduleViewMode = 'current';
        await this.loadPendingScheduleChangeRequests();
        await this.loadApprovedScheduleChange();
        await this.loadCaregiverSchedule();
        // Select the newly created schedule (matches the request's effective_date)
        if (effectiveDate) {
          const newIdx = this.caregiverSchedules.findIndex(
            s => !s.is_upcoming && s.start_date === effectiveDate
          );
          if (newIdx >= 0) {
            this.selectSchedule(newIdx);
          }
        }
      } catch (error) {
        this.$notifyError(error.response?.data?.error || 'Error approving request');
      } finally {
        this.reviewSubmitting = false;
      }
    },

    async denyScheduleRequest() {
      this.reviewSubmitting = true;
      try {
        await scheduleChangeRequestsService.reviewRequest(
          this.selectedPendingRequest.id,
          'denied'
        );
        this.$notifySuccess(`Schedule${this.hasCurrentSchedule ? ' change' : ''} request denied`);
        this.selectedPendingRequestIndex = 0;
        this.adminScheduleViewMode = 'current';
        await this.loadPendingScheduleChangeRequests();
      } catch (error) {
        this.$notifyError(error.response?.data?.error || 'Error denying request');
      } finally {
        this.reviewSubmitting = false;
      }
    },

    // Inline Schedule Change Request Methods
    initializeRequestedSchedule() {
      // If no current schedule, default to today; otherwise 14 days out
      const startDate = new Date();
      if (this.hasCurrentSchedule) {
        startDate.setDate(startDate.getDate() + 14);
      }

      // Build the complete object before assigning for Vue 2 reactivity
      const schedule = {
        start_date: localDateString(startDate),
        end_date: null, // Ongoing
        reason: "",
      };

      // Find current schedule to copy from
      const currentSchedule = this.caregiverSchedules.find(s => s.id && !s.is_upcoming);

      if (currentSchedule) {
        // Copy from current schedule
        const converted = this.convertScheduleShiftsToLocal({ ...currentSchedule });
        this.dayConfigs.forEach((dayConfig) => {
          const key = dayConfig.key;
          schedule[`${key}_enabled`] = converted[`${key}_enabled`] || false;
          if (converted[`${key}_shifts`] && Array.isArray(converted[`${key}_shifts`])) {
            schedule[`${key}_shifts`] = converted[`${key}_shifts`].map(s => ({ start: s.start, end: s.end }));
          } else {
            schedule[`${key}_shifts`] = [];
          }
        });
      } else {
        // No current schedule - default to 9-5 Mon-Fri
        this.dayConfigs.forEach((dayConfig) => {
          const isWeekday = dayConfig.key !== "saturday" && dayConfig.key !== "sunday";
          schedule[`${dayConfig.key}_enabled`] = isWeekday;
          schedule[`${dayConfig.key}_shifts`] = isWeekday ? [{ start: "09:00", end: "17:00" }] : [];
        });
      }

      this.requestedScheduleChange = schedule;
    },

    getRequestedShiftsForDay(day) {
      const shiftsKey = `${day}_shifts`;
      if (!this.requestedScheduleChange[shiftsKey]) {
        this.requestedScheduleChange[shiftsKey] = [];
      }
      // Ensure there's at least one shift if the day is enabled
      if (
        this.requestedScheduleChange[`${day}_enabled`] &&
        this.requestedScheduleChange[shiftsKey].length === 0
      ) {
        this.requestedScheduleChange[shiftsKey].push({ start: "", end: "" });
      }
      return this.requestedScheduleChange[shiftsKey];
    },

    onRequestedDayEnabledChange(day, enabled) {
      const enabledKey = `${day}_enabled`;
      const shiftsKey = `${day}_shifts`;

      this.$set(this.requestedScheduleChange, enabledKey, enabled);

      if (
        enabled &&
        (!this.requestedScheduleChange[shiftsKey] ||
          this.requestedScheduleChange[shiftsKey].length === 0)
      ) {
        // Initialize with a default 9am-5pm shift
        this.$set(this.requestedScheduleChange, shiftsKey, [{ start: "09:00", end: "17:00" }]);
      } else if (!enabled) {
        // Clear shifts when disabled
        this.$set(this.requestedScheduleChange, shiftsKey, []);
      }
    },

    updateRequestedShiftTime(day, shiftIndex, timeType, value) {
      const shiftsKey = `${day}_shifts`;
      if (!this.requestedScheduleChange[shiftsKey]) {
        this.$set(this.requestedScheduleChange, shiftsKey, []);
      }
      if (!this.requestedScheduleChange[shiftsKey][shiftIndex]) {
        this.$set(this.requestedScheduleChange[shiftsKey], shiftIndex, {
          start: "",
          end: "",
        });
      }
      this.$set(this.requestedScheduleChange[shiftsKey][shiftIndex], timeType, value);
    },

    addRequestedShift(day) {
      const shiftsKey = `${day}_shifts`;
      if (!this.requestedScheduleChange[shiftsKey]) {
        this.$set(this.requestedScheduleChange, shiftsKey, []);
      }
      this.requestedScheduleChange[shiftsKey].push({ start: "", end: "" });
    },

    removeRequestedShift(day, shiftIndex) {
      const shiftsKey = `${day}_shifts`;
      if (this.requestedScheduleChange[shiftsKey]) {
        this.requestedScheduleChange[shiftsKey].splice(shiftIndex, 1);
      }
    },


    async submitScheduleChangeRequest() {
      if (!this.isRequestedScheduleValid) {
        // Provide specific error messages
        if (!this.requestedScheduleChange.start_date) {
          this.$notifyError("Please select a start date");
          return;
        }

        const [y, m, d] = this.requestedScheduleChange.start_date.split('-').map(Number);
        const selectedDate = new Date(y, m - 1, d);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (this.hasCurrentSchedule) {
          const minDate = new Date(today);
          minDate.setDate(minDate.getDate() + 14);
          if (selectedDate < minDate) {
            this.$notifyError("Start date must be at least 14 days from today");
            return;
          }
        } else if (selectedDate < today) {
          this.$notifyError("Start date must be today or in the future");
          return;
        }

        if (
          this.hasCurrentSchedule && (
          !this.requestedScheduleChange.reason ||
          this.requestedScheduleChange.reason.trim() === ""
          )
        ) {
          this.$notifyError("Please provide a reason for the schedule change");
          return;
        }

        this.$notifyError(
          "Please ensure at least one day is enabled with valid start and end times",
        );
        return;
      }

      try {
        this.$spinner.show();

        // Build requested_schedule object with only enabled days (non-empty shifts)
        const requested_schedule = {};
        this.dayConfigs.forEach((dayConfig) => {
          const key = dayConfig.key;
          const enabledKey = `${key}_enabled`;
          const shiftsKey = `${key}_shifts`;

          if (this.requestedScheduleChange[enabledKey]) {
            const shifts = (this.requestedScheduleChange[shiftsKey] || [])
              .filter(s => s.start && s.end);
            if (shifts.length > 0) {
              requested_schedule[shiftsKey] = shifts;
            }
          }
        });

        const scheduleData = {
          effective_date: this.requestedScheduleChange.start_date,
          requested_schedule,
          reason: this.requestedScheduleChange.reason,
        };

        await scheduleChangeRequestsService.create(scheduleData);

        this.$notifySuccess(
          `Schedule${this.hasCurrentSchedule ? ' change' : ''} request submitted successfully! An admin will review it.`,
        );

        // Reload pending requests so the new one appears
        await this.loadPendingScheduleChangeRequests();

        // Switch to pending view (or current if they have a current schedule)
        if (this.hasCurrentSchedule) {
          this.scheduleViewMode = "current";
        } else {
          this.scheduleViewMode = "pending";
        }
        this.initializeRequestedSchedule();
      } catch (error) {
        this.$notifyError(
          `Error submitting schedule${this.hasCurrentSchedule ? ' change' : ''} request: ` +
            (error.message || error),
        );
      } finally {
        this.$spinner.hide();
      }
    },

    // Parse a date string as local time to avoid timezone shifts on YYYY-MM-DD strings
    parseDateLocal(dateString) {
      if (!dateString) return new Date(NaN);
      const str = String(dateString);
      if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
        const [y, m, d] = str.split('-').map(Number);
        return new Date(y, m - 1, d);
      }
      // Full datetime strings with T or Z - check if it's a midnight UTC that represents a date-only
      if (/^\d{4}-\d{2}-\d{2}T00:00:00/.test(str)) {
        const [y, m, d] = str.substring(0, 10).split('-').map(Number);
        return new Date(y, m - 1, d);
      }
      return new Date(dateString);
    },
    formatDateDisplay(dateString) {
      if (!dateString) return "";
      // For date-only strings (YYYY-MM-DD), parse as local time to avoid timezone shifts
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [y, m, d] = dateString.split('-').map(Number);
        return new Date(y, m - 1, d).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      }
      // For full datetime strings (ISO format), parse directly
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    // Training methods
    async loadTrainingTabData() {
      // Only load if not already loaded
      if (this.trainingRecords && this.trainingRecords.length > 0) return;
      await Promise.all([
        this.loadTrainingRecords(),
        this.loadTrainingDocuments(),
        this.loadTrainingCertificateRecords(),
      ]);
    },

    async loadTrainingRecords() {
      this.loadingTraining = true;
      try {
        this.trainingRecords = await caregiverTrainingService.getByCaregiver(
          this.caregiverId,
        );
      } catch (error) {
        this.$notifyError("Error loading training records: " + error);
      } finally {
        this.loadingTraining = false;
      }
    },

    async approveTraining(record) {
      try {
        this.$spinner.show();
        await caregiverTrainingService.review(record.id, "approved");
        this.$notifySuccess("Training approved successfully");
        await this.loadTrainingRecords(); // Reload the training records
      } catch (error) {
        this.$notifyError("Error approving training: " + error);
      } finally {
        this.$spinner.hide();
      }
    },

    async rejectTraining(record) {
      try {
        this.$spinner.show();
        await caregiverTrainingService.review(record.id, "rejected");
        this.$notifySuccess("Training rejected");
        await this.loadTrainingRecords(); // Reload the training records
      } catch (error) {
        this.$notifyError("Error rejecting training: " + error);
      } finally {
        this.$spinner.hide();
      }
    },

    async loadTrainingDocuments() {
      this.loadingDocuments = true;
      try {
        this.trainingDocuments = await documentsService.getCaregiverDocuments(this.caregiverId);
      } catch (error) {
        this.$notifyError("Error loading training documents: " + error);
      } finally {
        this.loadingDocuments = false;
      }
    },

    // Manual training entry methods
    onManualTrainingFileChange(event) {
      this.manualTrainingFile = event.target.files[0] || null;
    },

    async submitManualTraining() {
      if (!this.canSubmitManualTraining) return;
      this.submittingManualTraining = true;
      try {
        const record = await trainingRecordsService.create({
          id_users: this.caregiverId,
          training_date: this.manualTraining.training_date,
          training_type: this.manualTraining.training_type || null,
          training_title: this.manualTraining.training_title || null,
          hours_completed: parseFloat(this.manualTraining.hours_completed),
          summary: this.manualTraining.summary || null,
        });

        // Upload supporting document if provided
        if (this.manualTrainingFile && record.id) {
          try {
            await trainingRecordsService.uploadProof(record.id, this.manualTrainingFile);
          } catch (uploadErr) {
            this.$notifyWarning("Training saved but document upload failed: " + uploadErr);
          }
        }

        this.$notifySuccess("Training record added successfully");
        // Reset form
        this.manualTraining = { training_date: "", training_type: "", training_title: "", hours_completed: "", summary: "" };
        this.manualTrainingFile = null;
        if (this.$refs.manualTrainingFileInput) {
          this.$refs.manualTrainingFileInput.value = "";
        }
        this.showManualTrainingForm = false;
        // Reload records to show the new entry
        await this.loadTrainingCertificateRecords();
      } catch (error) {
        this.$notifyError("Error saving training record: " + (error.message || error));
      } finally {
        this.submittingManualTraining = false;
      }
    },

    // Training certificate records methods
    async loadTrainingCertificateRecords() {
      this.loadingTrainingRecords = true;
      try {
        let startDate, endDate;
        if (this.selectedTrainingYear) {
          [startDate, endDate] = this.selectedTrainingYear.split("|");
        }
        const response = await trainingRecordsService.getByCaregiver(
          this.caregiverId,
          startDate,
          endDate,
        );
        this.trainingCertificateRecords = response.records || [];
        this.trainingRecordsTotalHours = response.totalHours || 0;
        this.trainingYearStart = response.trainingYearStart || "";
        this.trainingYearEnd = response.trainingYearEnd || "";

        // Auto-select the current training year in the dropdown on first load
        if (!this.selectedTrainingYear && response.trainingYearStart && response.trainingYearEnd) {
          const start = response.trainingYearStart;
          const end = response.trainingYearEnd;
          // Convert MM/DD/YYYY to YYYY-MM-DD for the option id
          const toISO = (mdy) => {
            const [m, d, y] = mdy.split("/");
            return `${y}-${m}-${d}`;
          };
          this.selectedTrainingYear = `${toISO(start)}|${toISO(end)}`;
        }
      } catch (error) {
        this.$notifyError("Error loading training certificate records: " + error);
      } finally {
        this.loadingTrainingRecords = false;
      }
    },

    onTrainingYearChange() {
      this.loadTrainingCertificateRecords();
    },

    truncateSummary(text) {
      if (!text) return "-";
      return text.length > 60 ? text.substring(0, 60) + "..." : text;
    },

    getCertificateUrl(recordId) {
      return `/api/trainingRecords/${recordId}/certificate`;
    },

    getProofUrl(recordId) {
      return `/api/trainingRecords/${recordId}/proof`;
    },

    async uploadTrainingProof(recordId, event) {
      const file = event.target.files[0];
      if (!file) return;
      try {
        this.$spinner.show();
        await trainingRecordsService.uploadProof(recordId, file);
        this.$notifySuccess("External proof uploaded successfully");
        await this.loadTrainingCertificateRecords();
      } catch (error) {
        this.$notifyError("Error uploading proof: " + error);
      } finally {
        this.$spinner.hide();
      }
    },

    onTrainingFileChange(event) {
      const file = event.target.files[0] || null;
      this.newDocument.file = file;
      if (file && !this.newDocument.displayName) {
        this.newDocument.displayName = file.name;
      }
    },

    async uploadTrainingDocument() {
      if (!this.canUpload) return;

      this.uploadingDocument = true;
      try {
        if (this.newDocument.type === "file") {
          const formData = new FormData();
          formData.append("file", this.newDocument.file);
          formData.append("caregiverId", this.caregiverId);
          formData.append("displayName", this.newDocument.displayName);

          const result = await documentsService.uploadCaregiver(formData);
          if (result.document) {
            this.trainingDocuments.unshift(result.document);
          }
          this.$notifySuccess("Document uploaded successfully");
        } else {
          const result = await documentsService.addCaregiverLink({
            caregiverId: this.caregiverId,
            displayName: this.newDocument.displayName,
            url: this.newDocument.url,
          });
          if (result.document) {
            this.trainingDocuments.unshift(result.document);
          }
          this.$notifySuccess("Link added successfully");
        }

        // Reset form
        this.newDocument = { displayName: "", type: "file", url: "", file: null };
        if (this.$refs.trainingFileInput) {
          this.$refs.trainingFileInput.value = "";
        }
      } catch (error) {
        this.$notifyError("Error: " + (error.message || error));
      } finally {
        this.uploadingDocument = false;
      }
    },

    viewTrainingDocument(doc) {
      if (doc.is_link) {
        let url = doc.link;
        if (!url.startsWith("http://") && !url.startsWith("https://")) {
          url = "https://" + url;
        }
        window.open(url, "_blank");
      } else {
        // Fetch and open the file via authenticated endpoint
        const apiBaseUrl = process.env.VUE_APP_API_URL || window.location.origin;
        const filename = doc.link.split("/").pop();
        const fileUrl = `${apiBaseUrl}/api/pdf/client-document/${encodeURIComponent(filename)}`;
        const token = localStorage.getItem("jwtToken");

        fetch(fileUrl, {
          headers: { Authorization: token },
        })
          .then((response) => response.blob())
          .then((blob) => {
            const url = URL.createObjectURL(blob);
            window.open(url, "_blank");
          })
          .catch((err) => {
            this.$notifyError("Error opening document: " + err.message);
          });
      }
    },

    async deleteTrainingDocument(doc) {
      if (!confirm(`Are you sure you want to delete "${doc.display_name || 'this document'}"?`)) {
        return;
      }

      try {
        await documentsService.deleteCaregiverDocument(doc.id);
        this.trainingDocuments = this.trainingDocuments.filter((d) => d.id !== doc.id);
        this.$notifySuccess("Document deleted successfully");
      } catch (error) {
        this.$notifyError("Error deleting document: " + (error.message || error));
      }
    },

    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    // For DATE-only columns (e.g. training_date "2026-07-22"): parsing those
    // with new Date() lands on UTC midnight, which renders as the PREVIOUS
    // day (with a bogus time) in any US timezone. Parse the parts as a plain
    // local date instead and show no time.
    formatDateOnly(dateString) {
      if (!dateString) return "-";
      const m = String(dateString).match(/^(\d{4})-(\d{2})-(\d{2})/);
      const date = m
        ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
        : new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    formatDateShort(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    // Client access methods
    async loadClientAccess() {
      this.loadingClientAccess = true;
      try {
        this.clientAccessRecords = await userClientAccessService.getByUser(this.caregiverId);
      } catch (error) {
        this.$notifyError("Error loading client access: " + error);
      } finally {
        this.loadingClientAccess = false;
      }
    },

    async revokeClientAccess(record) {
      if (!record.client) return;
      const name = `${record.client.first_name} ${record.client.last_name}`;
      if (!confirm(`Revoke access to ${name}?`)) return;
      try {
        await userClientAccessService.revoke(this.caregiverId, record.client.id);
        this.$notifySuccess(`Access to ${name} revoked`);
        await this.loadClientAccess();
      } catch (error) {
        this.$notifyError("Error revoking access: " + error);
      }
    },

    async grantClientAccessBulk() {
      if (this.selectedClientsToGrant.length === 0) return;
      try {
        await userClientAccessService.grantBulk(this.caregiverId, this.selectedClientsToGrant);
        this.$notifySuccess(`Access granted to ${this.selectedClientsToGrant.length} client(s)`);
        this.showGrantClientAccessModal = false;
        this.selectedClientsToGrant = [];
        await this.loadClientAccess();
      } catch (error) {
        this.$notifyError("Error granting access: " + error);
      }
    },
  },
};
</script>

<style scoped>
.compliance-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 4rem 2rem;
  color: #888;
}

.compliance-loading i {
  color: #6ba6cd;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 30px;
  flex-wrap: wrap;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
  flex: 1;
}

.header-right {
  display: flex;
  align-items: center;
  margin-left: auto;
}

.back-button {
  font-size: 14px;
  padding: 8px 16px;
  height: 40px;
}

.back-button i {
  margin-right: 6px;
}

.btn-change-password {
  background: rgba(23, 162, 184, 0.2);
  color: #17a2b8;
  border: 1px solid rgba(23, 162, 184, 0.4);
  padding: 0.5rem 1rem;
  border-radius: 0.25rem;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 40px;
}

.btn-change-password:hover {
  background: rgba(23, 162, 184, 0.3);
  border-color: rgba(23, 162, 184, 0.6);
  color: #17a2b8;
}

.btn-change-password i {
  font-size: 0.875rem;
}

.caregiver-name {
  margin: 0;
  flex: 0 0 auto;
}

.button-toggle-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0;
  margin: 0;
  margin-top: -3px;
}

.toggle-button {
  border-radius: 0;
  min-width: 120px;
  margin: 0;
}

.toggle-button:first-child {
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
}

.toggle-button:last-child {
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
}

.toggle-button:not(:first-child) {
  border-left: none;
}

:deep(.toggle-button.el-button--default) {
  background-color: #606266;
  border-color: #606266;
  color: #fff;
}

:deep(.toggle-button.el-button--default:hover) {
  background-color: #737373;
  border-color: #737373;
  color: #fff;
}

:deep(.toggle-button.el-button--primary) {
  background-color: #409eff;
  border-color: #409eff;
  color: #fff;
}

.caregiver-tab-content {
  margin-top: 1rem;
}

.caregiver-tab-pane {
  padding: 0;
  color: #fff;
  min-height: 200px;
}

.caregiver-tab-pane > p {
  color: #fff;
  font-size: 1rem;
  margin: 0;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
}

@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .header-left {
    width: 100%;
  }

  .header-right {
    width: 100%;
    margin-left: 0;
  }

  .button-toggle-row {
    width: 100%;
  }

  .toggle-button {
    flex: 1;
  }
}

.caregiver-schedule-container {
  width: 100%;
}

.modern-calendar-wrapper {
  width: 100%;
}

.calendar-header-actions {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 1rem;
  padding: 0.5rem 0;
}

.add-shift-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Modern Legend Row */
.modern-legend-row {
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.legend-groups {
  display: flex;
  align-items: center;
  gap: 2rem;
  flex-wrap: wrap;
  justify-content: center;
}

.legend-group {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.legend-group-header {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: #6ba6cd;
  letter-spacing: 0.025em;
  text-transform: uppercase;
  flex-shrink: 0;
}

.legend-group-header i {
  font-size: 0.75rem;
  width: 12px;
  text-align: center;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.9);
  padding: 0.25rem 0;
  transition: color 0.2s ease;
}

.legend-item:hover {
  color: #fff;
}

.legend-color {
  width: 14px;
  height: 10px;
  border-radius: 2px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s ease;
}

.legend-item:hover .legend-color {
  transform: scale(1.1);
}

/* Status Colors */
.legend-color.status-scheduled {
  background: linear-gradient(
    135deg,
    rgba(13, 110, 253, 0.8),
    rgba(13, 110, 253, 0.6)
  );
}

.legend-color.status-clocked-in {
  background: linear-gradient(
    135deg,
    rgba(255, 193, 7, 0.9),
    rgba(255, 193, 7, 0.7)
  );
}

.legend-color.status-clocked-out {
  background: linear-gradient(
    135deg,
    rgba(237, 137, 54, 0.7),
    rgba(237, 137, 54, 0.5)
  );
}

.legend-color.status-approved {
  background: linear-gradient(
    135deg,
    rgba(156, 39, 176, 0.7),
    rgba(156, 39, 176, 0.5)
  );
}

.legend-color.status-missed {
  background: linear-gradient(
    135deg,
    rgba(108, 117, 125, 0.8),
    rgba(108, 117, 125, 0.6)
  );
}

.legend-color.work-schedule {
  background: linear-gradient(
    135deg,
    rgba(64, 158, 255, 0.3),
    rgba(64, 158, 255, 0.15)
  );
}

.legend-color.time-off {
  background: rgba(255, 152, 0, 0.2);
  border: 1px solid rgba(255, 152, 0, 0.6);
}

/* Month Navigation */
.month-navigation {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  margin: 1rem 0;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.8);
  padding: 0;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 36px;
  width: 36px;
}

.nav-btn:hover {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.25);
  color: #fff;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.nav-btn i {
  font-size: 12px;
}

.current-month-display {
  font-size: 1.25rem;
  font-weight: 600;
  color: #fff;
  min-width: 200px;
  text-align: center;
  letter-spacing: 0.5px;
}

.calendar-view {
  margin-top: 1rem;
  width: 100%;
}

/* Calendar-specific styles */
:deep(.custom-calendar) {
  width: 100%;
}

:deep(.calendar-grid) {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
}

:deep(.calendar-grid.with-weekly-summary) {
  grid-template-columns: repeat(8, minmax(0, 1fr));
}

:deep(.day-name) {
  font-weight: bold;
  text-align: center;
  border-right: 1px solid rgba(255, 255, 255, 0.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  padding: 8px;
  color: #fff;
  background: rgba(255, 255, 255, 0.05);
}

:deep(.calendar-day) {
  position: relative;
  background: transparent;
  border-right: 1px solid rgba(255, 255, 255, 0.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  min-height: 80px;
  padding: 4px;
  cursor: pointer;
}

/* Default 7-column layout */
:deep(.calendar-day:nth-child(7n)) {
  border-right: none;
}

:deep(.calendar-day:nth-last-child(-n + 7)) {
  border-bottom: none;
}

:deep(.day-name:nth-child(7n)) {
  border-right: none;
}

/* 8-column layout when weekly summary is shown */
:deep(.calendar-grid.with-weekly-summary .calendar-day:nth-child(7n)) {
  border-right: 1px solid rgba(255, 255, 255, 0.2);
}

:deep(.calendar-grid.with-weekly-summary .calendar-day:nth-child(8n)) {
  border-right: none;
}

:deep(.calendar-grid.with-weekly-summary .calendar-day:nth-last-child(-n + 8)) {
  border-bottom: none;
}

:deep(.calendar-grid.with-weekly-summary .day-name:nth-child(7n)) {
  border-right: 1px solid rgba(255, 255, 255, 0.2);
}

:deep(.calendar-grid.with-weekly-summary .day-name:nth-child(8n)) {
  border-right: none;
}

:deep(.date-number) {
  font-size: 0.85rem;
  font-weight: bold;
  color: #fff;
}

:deep(.today) {
  background-color: rgba(107, 166, 205, 0.2);
  border: 2px solid #6ba6cd;
}

:deep(.other-month) {
  opacity: 0.4;
}

:deep(.shift-item) {
  margin-top: 2px;
  font-size: 0.7rem;
  color: #fff;
  padding: 4px 6px;
  min-height: 24px;
  border-radius: 3px;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 4px;
}

:deep(.shift-time) {
  font-weight: bold;
  flex-shrink: 0;
}

:deep(.shift-client) {
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: 0.9;
}

:deep(.shift-caregiver) {
  font-size: 0.6rem;
  color: #e0e0e0;
  font-style: italic;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  opacity: 0.8;
}

/* Work Schedule Editor Styles - matching Caregiver modal */
.schedule-editor {
  padding: 1rem;
  background: transparent;
  border-radius: 8px;
}

/* Schedule Management Section */
.schedule-management {
  margin-bottom: 1.5rem;
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.03);
}

.schedule-selector {
  margin-bottom: 1rem;
}

.schedule-tabs {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: 0.5rem;
}

.schedule-tab {
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  color: #fff;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
}

.schedule-tab:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.3);
}

.schedule-tab.active {
  background: #409eff;
  border-color: #409eff;
  color: #fff;
}


.schedule-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}


.action-btn {
  padding: 0.4rem 0.8rem;
  font-size: 0.75rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.3rem;
}

.duplicate-btn {
  background: rgba(23, 162, 184, 0.2);
  color: #17a2b8;
  border: 1px solid rgba(23, 162, 184, 0.4);
}

.duplicate-btn:hover {
  background: rgba(23, 162, 184, 0.3);
  border-color: rgba(23, 162, 184, 0.6);
}

.delete-btn {
  background: rgba(220, 53, 69, 0.2);
  color: #dc3545;
  border: 1px solid rgba(220, 53, 69, 0.4);
}

.delete-btn:hover {
  background: rgba(220, 53, 69, 0.3);
  border-color: rgba(220, 53, 69, 0.6);
}

/* Pending Schedule Change Requests Section */
.pending-requests-section {
  margin-bottom: 1.5rem;
  padding: 1rem;
  border: 1px solid rgba(255, 200, 0, 0.3);
  border-radius: 8px;
  background: rgba(255, 200, 0, 0.05);
}

.pending-requests-section .section-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.pending-requests-section .section-header i {
  color: #ffc107;
  font-size: 1.2rem;
}

.pending-requests-section .section-header .time-label {
  color: #ffc107;
  font-weight: 600;
  margin: 0;
}

.requests-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.request-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  transition: all 0.2s;
}

.request-item:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.2);
}

.request-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.request-info > div {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.9);
}

.request-info strong {
  color: rgba(255, 255, 255, 0.7);
  margin-right: 0.3rem;
}

.request-actions {
  display: flex;
  gap: 0.5rem;
}

.request-actions .btn {
  white-space: nowrap;
}

/* Date Range Section */
.schedule-date-range {
  margin-bottom: 1.5rem;
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.03);
}

.date-range-grid {
  display: flex;
  gap: 1.5rem;
  align-items: flex-end;
}

.date-input-group {
  flex: 1;
}

.time-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: #409eff;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0;
}

.date-input {
  width: 100%;
  height: 32px;
  padding: 0 8px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  color: #fff;
  font-size: 12px;
  margin-top: 4px;
}

.date-input:hover {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.3);
}

.date-input:focus {
  outline: none;
  border-color: #409eff;
  background: rgba(255, 255, 255, 0.15);
}

.date-input::-webkit-calendar-picker-indicator {
  filter: invert(1);
  opacity: 0.7;
}

.date-input::-webkit-calendar-picker-indicator:hover {
  opacity: 1;
}

/* Calendar-style grid matching Calendar page */
.schedule-calendar-grid {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 0;
  margin-bottom: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: transparent;
}

/* Day headers - matching Calendar page styling */
.schedule-day-header {
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  border-right: 1px solid rgba(255, 255, 255, 0.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  padding: 8px;
  color: #fff;
  background: rgba(255, 255, 255, 0.05);
  font-size: 0.875rem;
}

.schedule-day-header:nth-child(8n) {
  border-right: none;
}

/* Day cells - matching Calendar page */
.schedule-day-cell {
  position: relative;
  background: transparent;
  border-right: 1px solid rgba(255, 255, 255, 0.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  min-height: 120px;
  padding: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.schedule-day-cell:nth-child(8n + 8) {
  border-right: none;
}

.schedule-day-cell:hover {
  background: rgba(255, 255, 255, 0.03);
}

.day-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  height: 100%;
  align-items: center;
  justify-content: center;
  padding: 8px;
  position: relative;
}

.day-content.day-enabled {
  background: rgba(64, 158, 255, 0.05);
  border: 1px solid rgba(64, 158, 255, 0.2);
  border-radius: 4px;
}

/* Checkbox styling */
.header-checkbox-label {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  cursor: pointer;
  position: relative;
}

.header-checkbox-label span:first-child {
  font-weight: bold;
  color: #fff;
}

.header-checkbox-label input[type="checkbox"] {
  opacity: 0;
  position: absolute;
  cursor: pointer;
}

.schedule-checkmark {
  height: 16px;
  width: 16px;
  background-color: transparent;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-radius: 3px;
  position: relative;
  transition: all 0.2s ease;
}

.header-checkbox-label:hover .schedule-checkmark {
  border-color: #409eff;
  background: rgba(64, 158, 255, 0.1);
}

.header-checkbox-label input:checked ~ .schedule-checkmark {
  background-color: #409eff;
  border-color: #409eff;
}

.header-checkbox-label input:checked ~ .schedule-checkmark:after {
  content: "";
  position: absolute;
  display: block;
  left: 4px;
  top: 1px;
  width: 3px;
  height: 7px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.day-disabled-text {
  color: rgba(255, 255, 255, 0.3);
  font-size: 0.85rem;
  font-style: italic;
}

/* Multiple Shifts Controls */
.multiple-shifts-controls {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
}

.shift-row {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.25rem;
  background: linear-gradient(
    135deg,
    rgba(64, 158, 255, 0.15),
    rgba(64, 158, 255, 0.08)
  );
  border-radius: 4px;
  border: 1px solid rgba(64, 158, 255, 0.2);
}

.shift-remove-row {
  display: flex;
  justify-content: flex-end;
}

.shift-times {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
}

.time-input-group {
  width: 100%;
}

.remove-shift-btn {
  background: rgba(220, 53, 69, 0.2);
  color: #dc3545;
  border: 1px solid rgba(220, 53, 69, 0.4);
  border-radius: 3px;
  padding: 2px 6px;
  font-size: 0.7rem;
  cursor: pointer;
  transition: all 0.2s;
  height: 22px;
  min-width: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.remove-shift-btn:hover {
  background: rgba(220, 53, 69, 0.3);
  border-color: rgba(220, 53, 69, 0.6);
}

.add-shift-btn {
  background: rgba(40, 167, 69, 0.2);
  color: #28a745;
  border: 1px solid rgba(40, 167, 69, 0.4);
  border-radius: 4px;
  padding: 0.3rem 0.5rem;
  font-size: 0.7rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  justify-content: center;
  margin-top: 0.25rem;
}

.add-shift-btn:hover {
  background: rgba(40, 167, 69, 0.3);
  border-color: rgba(40, 167, 69, 0.6);
}

:deep(.time-input-group .el-input--mini .el-input__inner) {
  height: 28px !important;
  line-height: 28px !important;
  font-size: 11px !important;
  padding: 0 8px !important;
  background: rgba(255, 255, 255, 0.08) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  color: #fff !important;
  border-radius: 4px !important;
}

:deep(.time-input-group .el-input--mini .el-input__inner:hover) {
  background: rgba(255, 255, 255, 0.12) !important;
  border-color: rgba(255, 255, 255, 0.3) !important;
}

:deep(.time-input-group .el-input--mini .el-input__inner:focus) {
  border-color: #409eff !important;
  background: rgba(255, 255, 255, 0.15) !important;
}

:deep(.time-input-group .el-input--mini .el-input__inner::placeholder) {
  color: rgba(255, 255, 255, 0.4) !important;
  font-size: 11px !important;
}

.notes-section {
  margin-top: 2rem;
}

.notes-section label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #fff;
}

.notes-textarea {
  width: 100%;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  color: #fff;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  margin-top: 0.5rem;
}

.notes-textarea:hover {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.3);
}

.notes-textarea:focus {
  outline: none;
  border-color: #409eff;
  background: rgba(255, 255, 255, 0.15);
}

.notes-textarea::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

.notes-display {
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  white-space: pre-wrap;
  margin-top: 0.5rem;
}

/* Schedule Mode Selector */
.schedule-mode-selector {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
}

/* Schedule Action Buttons */
.schedule-action-buttons {
  margin-bottom: 1rem;
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

/* Bulk Edit Checkbox */
.bulk-edit-checkbox {
  position: absolute;
  top: 5px;
  right: 5px;
  z-index: 10;
}

.bulk-edit-checkbox input[type="checkbox"] {
  cursor: pointer;
  width: 16px;
  height: 16px;
}

/* Schedule Submit Actions */
.schedule-submit-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.text-center {
  text-align: center;
  padding: 2rem;
  color: rgba(255, 255, 255, 0.7);
}

/* Calendar responsive styles */
@media (max-width: 768px) {
  .modern-legend-row {
    padding: 0.5rem;
  }

  .legend-groups {
    gap: 1rem;
  }

  .month-navigation {
    gap: 1rem;
    padding: 0.5rem;
  }

  .current-month-display {
    font-size: 1rem;
    min-width: 150px;
  }

  .calendar-view {
    padding: 0.25rem;
  }

  :deep(.calendar-day) {
    min-height: 80px !important;
    padding: 4px !important;
  }

  :deep(.shift-item) {
    font-size: 0.6rem !important;
    padding: 2px 3px !important;
  }

  :deep(.shift-client) {
    display: none !important;
  }

  :deep(.shift-caregiver) {
    display: none !important;
  }

  :deep(.day-name) {
    font-size: 0.65rem !important;
    padding: 6px 2px !important;
  }

  :deep(.date-number) {
    font-size: 0.75rem !important;
  }
}

@media (max-width: 480px) {
  :deep(.calendar-day) {
    min-height: 60px !important;
    padding: 2px !important;
  }

  :deep(.shift-time) {
    font-size: 0.55rem !important;
  }
}

/* Schedule editor responsive styles - matching Caregiver modal */
@media (max-width: 1024px) {
  .schedule-calendar-grid {
    grid-template-columns: repeat(8, 1fr);
  }

  .schedule-day-cell {
    min-height: 100px;
  }

  .schedule-day-header {
    font-size: 0.75rem;
    padding: 6px 4px;
  }

  .header-checkbox-label {
    gap: 6px;
  }

  .header-checkbox-label span:first-child {
    font-size: 0.7rem;
  }

  .schedule-checkmark {
    height: 14px;
    width: 14px;
  }

  :deep(.time-input-group .el-input--mini .el-input__inner) {
    font-size: 10px !important;
    padding: 0 4px !important;
  }
}

@media (max-width: 768px) {
  .schedule-calendar-grid {
    grid-template-columns: repeat(8, 1fr);
    font-size: 0.75rem;
  }

  .schedule-day-cell {
    min-height: 90px;
    padding: 4px;
  }

  .schedule-day-header {
    font-size: 0.65rem;
    padding: 6px 2px;
  }

  .header-checkbox-label {
    gap: 4px;
  }

  .header-checkbox-label span:first-child {
    font-size: 0.6rem;
  }

  :deep(.time-input-group .el-input--mini .el-input__inner) {
    height: 24px !important;
    line-height: 24px !important;
    font-size: 9px !important;
    padding: 0 2px !important;
  }

  :deep(.time-input-group .el-input--mini .el-input__inner::placeholder) {
    font-size: 9px !important;
  }
}

@media (max-width: 480px) {
  .date-range-grid {
    flex-direction: column;
    gap: 1rem;
  }

  .schedule-date-range {
    padding: 0.75rem;
  }

  .date-input {
    height: 28px;
    font-size: 11px;
  }

  .schedule-calendar-grid {
    grid-template-columns: repeat(8, 1fr);
    overflow-x: auto;
    min-width: 480px;
  }

  .schedule-editor {
    padding: 0.5rem;
    overflow-x: auto;
  }

  .schedule-day-cell {
    min-height: 80px;
    min-width: 60px;
  }

  .schedule-day-header {
    font-size: 0.6rem;
    padding: 4px 2px;
    min-width: 60px;
  }
}

/* Details tab styles */
.caregiver-details-content {
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
}

.caregiver-details-content :deep(.el-form-item__label) {
  color: rgba(255, 255, 255, 0.85);
  font-weight: 500;
}

.caregiver-details-content :deep(.el-input__inner),
.caregiver-details-content :deep(.el-textarea__inner) {
  background-color: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.2);
  color: #fff;
}

.caregiver-details-content :deep(.el-input__inner:focus),
.caregiver-details-content :deep(.el-textarea__inner:focus) {
  background-color: rgba(255, 255, 255, 0.12);
  border-color: #6ba6cd;
}

.caregiver-details-content :deep(.el-input__inner::placeholder),
.caregiver-details-content :deep(.el-textarea__inner::placeholder) {
  color: rgba(255, 255, 255, 0.4);
}

.caregiver-details-content :deep(.el-input.is-disabled .el-input__inner),
.caregiver-details-content :deep(.el-textarea.is-disabled .el-textarea__inner) {
  background-color: rgba(255, 255, 255, 0.03);
  color: rgba(255, 255, 255, 0.6);
  cursor: not-allowed;
}

/* Schedule Change Request Button */
.schedule-change-request-header {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 1rem;
  padding: 0.5rem 0;
}

.request-schedule-change-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* View-Only Schedule Styles */
.schedule-viewer {
  padding: 1rem;
  background: transparent;
  border-radius: 8px;
}

.schedule-calendar-grid.view-only .schedule-day-cell {
  cursor: default;
}

.schedule-calendar-grid.view-only .schedule-day-cell:hover {
  background: transparent;
}

.multiple-shifts-display {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
}

.shift-display-row {
  padding: 0.5rem;
  background: linear-gradient(
    135deg,
    rgba(64, 158, 255, 0.3),
    rgba(64, 158, 255, 0.15)
  );
  border-radius: 4px;
  border: 1px solid rgba(64, 158, 255, 0.3);
}

.shift-times-display {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;
  font-size: 0.875rem;
  color: #fff;
}

.time-display {
  font-weight: 600;
  color: #6ba6cd;
}

.time-separator {
  color: rgba(255, 255, 255, 0.6);
}

.date-display {
  height: 32px;
  padding: 0 8px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  color: #fff;
  font-size: 12px;
  margin-top: 4px;
  display: flex;
  align-items: center;
}

.notes-display {
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.875rem;
  white-space: pre-wrap;
  margin-top: 4px;
}

.schedule-date-range.view-only {
  background: rgba(255, 255, 255, 0.03);
}

/* Training Documents Section */
.training-section {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #444;
}

.training-section:first-child {
  margin-top: 0;
  padding-top: 0;
  border-top: none;
}

.training-section h5 {
  color: #fff;
  margin-bottom: 1rem;
  font-weight: 600;
}

.training-card {
  background-color: #2c3e50;
  border: 1px solid #444;
  border-radius: 8px;
  padding: 1rem;
}

.training-card label {
  color: #ccc;
  font-size: 0.85rem;
  margin-bottom: 0.25rem;
}

.client-access-section h5 {
  color: #fff;
  font-weight: 600;
}

.client-access-section .form-check-label {
  color: #ccc;
}

.hours-total-card {
  background: rgba(107, 166, 205, 0.15);
  border: 1px solid rgba(107, 166, 205, 0.3);
  border-radius: 8px;
  padding: 12px 20px;
  text-align: center;
}

.hours-total-label {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.85rem;
  margin-bottom: 4px;
}

.hours-total-value {
  color: #6ba6cd;
  font-size: 1.4rem;
  font-weight: 700;
}

.training-year-range {
  font-size: 0.85rem;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.6);
}

.training-summary-preview {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.85rem;
  cursor: default;
}

.training-card .form-control {
  background-color: #656565;
  border: 1px solid #bdbdbd;
  color: #e0e0e0;
  border-radius: 4px;
  font-size: 14px;
}

.documents-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.document-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
}

.document-icon {
  color: #6ba6cd;
  font-size: 1.1rem;
  width: 24px;
  text-align: center;
}

.document-info {
  flex: 1;
  min-width: 0;
}

.document-name {
  color: #6ba6cd;
  text-decoration: none;
  font-weight: 500;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.document-name:hover {
  color: #8ec4e8;
  text-decoration: underline;
}

.document-meta {
  color: #9b9b9b;
  font-size: 0.8rem;
}

.document-delete {
  flex-shrink: 0;
}
</style>
